/**
 * Page des tables de jeu
 * 
 * Hub principal pour :
 * - Voir les tables existantes par catégorie
 * - Créer une table libre (bouton "+") ou via le mode brouillon collaboratif
 * - Créer des défis (libre, équipe, CIA)
 * 
 * Layout:
 * - sidebar avec les catégories de tables
 * - zone principale : liste des tables OU éditeur de table brouillon
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import type { GameTable, TableList, TablesApiResponse } from '../types/game';
import {
  PlusIcon,
  LiveBroadcastIcon,
  HourglassTableIcon,
  BalloonIcon,
  CrossedSwordsTableIcon,
  MixteIcon,
  MaxiIcon,
  MaxiAttackIcon,
  AttackIcon,
  AttackSurContratIcon,
  DefenseIcon,
  MaxiDefenseIcon,
  ListGridIcon,
  EquipeIcon,
  TripleteIcon,
  TrophyIcon,
} from '../components/Icons';
import { useApp } from '../contexts/AppContext';
import { useTabReselect } from '../hooks/useTabReselect';
import { useResponsive } from '../hooks/useResponsive';
import MasterDetail from '../components/MasterDetail';
import type { Session } from '../types/app';
import type { DraftTableConfig } from '../types/draft';
import { useDraftTable } from '../hooks/useDraftTable';
import DraftTableEditor from '../components/DraftTableEditor';
import DraftCloseConfirm from '../components/DraftCloseConfirm';
import { PlayerAvatar, usePrefetchAvatars } from '../components/PlayerAvatar';
import '../styles/chat.css'; // Réutiliser les styles du chat
import '../styles/tables.css';
import '../styles/draft.css';
import { BACKEND_URL } from '../config';
import { isBotPseudo } from '../utils/pseudo';
import RetroDifferePage from './competitions/RetroDifferePage';
import SpectatorMultiplex from './SpectatorMultiplex';
import { useSpectatorSources } from '../hooks/useSpectatorPlayback';
import type { SpectatorMode } from '../hooks/useSpectatorPlayback';
import { useRetroDiffereTableInfo } from '../hooks/useRetroDiffereTableInfo';
import {
  defiKindLabel,
  enrichGameTableDefiMeta,
  resolveDefiKind,
  type DefiKind,
} from '../lib/defiKind';

/** Clé spéciale pour afficher toutes les tables */
const ALL_TABLES_KEY = '__all__';

/** Mode d'affichage de la zone principale */
type ViewMode = 'list' | 'draft' | 'spectatorMultiplex';

interface TablesProps {
  session: Session;
  isAdmin?: boolean;
}

const DEFI_KIND_SORT_ORDER: DefiKind[] = [
  'cia',
  'championnat_equipe',
  'par_equipe',
  'quadrette',
  'ouvert',
  'libre',
  'entrainement',
  'unknown',
];

function getTableDefiKind(table: GameTable): DefiKind {
  if (table.defiKind && table.defiKind !== 'unknown') return table.defiKind;
  return resolveDefiKind(table);
}

function getTableDefiLabel(table: GameTable): string {
  if (table.defiKindLabel) return table.defiKindLabel;
  return defiKindLabel(getTableDefiKind(table));
}

function sortDefiTables(tables: GameTable[]): GameTable[] {
  return [...tables].sort((a, b) => {
    const ka = DEFI_KIND_SORT_ORDER.indexOf(getTableDefiKind(a));
    const kb = DEFI_KIND_SORT_ORDER.indexOf(getTableDefiKind(b));
    if (ka !== kb) return ka - kb;
    return (a.nomtable || '').localeCompare(b.nomtable || '', 'fr');
  });
}

export function getDefiKindColor(kind: DefiKind | string): string {
  switch (kind) {
    case 'cia': return '#8b5cf6';
    case 'championnat_equipe': return '#d97706';
    case 'par_equipe': return '#f59e0b';
    case 'ouvert': return '#10b981';
    case 'quadrette': return '#6366f1';
    case 'libre': return '#f59e0b';
    case 'entrainement': return '#94a3b8';
    default: return '#f59e0b';
  }
}

// Couleur associée à chaque type de table
export function getTableTypeColor(idserv: string): string {
  switch (idserv) {
    case 'live': return '#ef4444';
    case 'diff_indiv': return '#0d9488';
    case 'diff_tq': return '#3b82f6';
    case 'libre': return '#a855f7';
    case 'defis': return '#f59e0b';
    case 'tournois': return '#e879f9';
    default: return '#6b7280';
  }
}

// Icône SVG pour les différents types de tables
export function getTableIcon(idserv: string, size: number = 20): React.ReactNode {
  switch (idserv) {
    case 'live': return <LiveBroadcastIcon size={size} />;
    case 'diff_indiv':
    case 'diff_tq': return <HourglassTableIcon size={size} />;
    case 'libre': return <BalloonIcon size={size} />;
    case 'defis': return <CrossedSwordsTableIcon size={size} />;
    case 'tournois': return <TrophyIcon size={size} />;
    default: return '🃏';
  }
}

export function getDefiIcon(defiType: string, size: number = 20): React.ReactNode {
  void defiType;
  return <CrossedSwordsTableIcon size={size} />;
}

// Icône SVG pour les sous-types de différé individuel (basé sur le titre extrait)
export function getDiffereSubTypeIcon(title: string, size: number = 16): React.ReactNode | null {
  const t = title.toLowerCase();
  if (t.includes('maxi') && (t.includes('déf') || t.includes('def'))) return <MaxiDefenseIcon size={size} />;
  if (t.includes('maxi') && t.includes('att')) return <MaxiAttackIcon size={size} />;
  if (t.includes('maxi')) return <MaxiIcon size={size} />;
  if (t.includes('100%') && t.includes('att') && (t.includes('sur') || t.includes('sc'))) return <AttackSurContratIcon size={size} />;
  if (t.includes('100%') && t.includes('att')) return <AttackIcon size={size} />;
  if (t.includes('100%') && (t.includes('déf') || t.includes('def'))) return <DefenseIcon size={size} />;
  if (t.includes('mixte')) return <MixteIcon size={size} />;
  return null;
}

// Détecte si un différé équipe/triplette est de type "équipe" ou "triplette" (depuis le nomtable brut)
function getDiffTqTypeIcon(nomtable: string | undefined, size: number = 14): React.ReactNode | null {
  if (!nomtable) return null;
  const decoded = decodeHtmlEntities(nomtable).toLowerCase();
  if (decoded.includes('équipe') || decoded.includes('equipe')) return <EquipeIcon size={size} />;
  if (decoded.includes('triplette')) return <TripleteIcon size={size} />;
  return null;
}

// Détecte le type de table depuis le nom d'une catégorie
function getCategoryIdserv(categoryName: string): string {
  const name = categoryName.toLowerCase();
  if (name.includes('live')) return 'live';
  if (name.includes('diff') && (name.includes('équipe') || name.includes('equipe') || name.includes('triplette'))) return 'diff_tq';
  if (name.includes('diff')) return 'diff_indiv';
  if (name.includes('libre')) return 'libre';
  if (name.includes('défi') || name.includes('defi')) return 'defis';
  if (name.includes('tournoi')) return 'tournois';
  return '';
}

function findCategoryKeyForIdserv(
  tables: TableList | null,
  idserv: 'libre' | 'defis',
): string | null {
  if (!tables) return null;

  for (const [key, categoryTables] of Object.entries(tables)) {
    if (categoryTables.some((t) => t.idserv === idserv)) return key;
    if (getCategoryIdserv(decodeHtmlEntities(key)) === idserv) return key;
  }

  for (const key of Object.keys(tables)) {
    const decoded = decodeHtmlEntities(key).toLowerCase();
    if (idserv === 'libre' && decoded.includes('libre') && !decoded.includes('défi') && !decoded.includes('defi')) {
      return key;
    }
    if (idserv === 'defis' && (decoded.includes('défi') || decoded.includes('defi'))) {
      return key;
    }
  }

  return null;
}

function idservForDraftGameMode(gameMode?: string): 'libre' | 'defis' {
  return gameMode === 'defi_libre' ? 'defis' : 'libre';
}

// Décode les entités HTML dans les noms de catégories
export function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// Décode le pseudo (¶ -> espace)
export function decodePseudo(pseudo: string): string {
  return pseudo.replace(/¶/g, ' ').trim();
}

/** Strip "X joueur(s) connecté(s)" and leading/trailing commas from comm text */
function stripConnectedCount(text: string): string {
  return text
    .replace(/,?\s*\d+\s+joueurs?\s+connect[ée]s?/gi, '')
    .replace(/^\s*,\s*/, '')
    .replace(/,\s*$/, '')
    .trim();
}

/** Retire une liste de pseudos collée en fin de commentaire (ex. « , panurge2, panurge ») */
function stripTrailingPseudoList(text: string): string {
  return text
    .replace(/(?:^|,\s*)(?:[A-Za-z0-9_¶.-]{2,}(?:\s*,\s*[A-Za-z0-9_¶.-]{2,})+)$/u, '')
    .replace(/,\s*$/, '')
    .trim();
}

const DEFI_PSEUDO_TOKEN = '[A-Za-z0-9_¶.-]{2,}';

function isSimpleDefiPseudo(s: string): boolean {
  return new RegExp(`^${DEFI_PSEUDO_TOKEN}$`, 'i').test(s);
}

function splitDefiPseudos(raw: string): string[] {
  return raw
    .split(',')
    .map((p) => decodePseudo(p.trim()))
    .filter((p) => p && !/^bot[1-9]$/i.test(p));
}

/** Extrait une composition « a, b, c[, d] » en fin de chaîne, laisse le statut devant. */
function extractTrailingLineup(text: string): { status: string; lineup: string[] } {
  const trimmed = text.trim();
  if (!trimmed) return { status: '', lineup: [] };
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return { status: trimmed, lineup: [] };

  const lineup: string[] = [];
  let i = parts.length - 1;
  while (i > 0 && isSimpleDefiPseudo(parts[i]) && lineup.length < 4) {
    lineup.unshift(decodePseudo(parts[i]));
    i -= 1;
  }

  const head = parts[i];
  const headMatch = head.match(new RegExp(`^(.*\\s)?(${DEFI_PSEUDO_TOKEN})$`, 'i'));
  const peeled = headMatch?.[2] || '';
  const statusWord = /^(minutes?|donne|score|dans|moins|debut|début|contre|joueurs?|connecte[sé]?|n°?\d+)$/i;
  if (headMatch && peeled && !statusWord.test(peeled) && lineup.length >= 2) {
    lineup.unshift(decodePseudo(peeled));
    const statusHead = (headMatch[1] || '').trim();
    const status = [...parts.slice(0, i), statusHead].filter(Boolean).join(', ').trim();
    return {
      status,
      lineup: lineup.filter((p) => !/^bot[1-9]$/i.test(p)),
    };
  }

  if (lineup.length >= 3) {
    return {
      status: parts.slice(0, i + 1).join(', ').trim(),
      lineup: lineup.filter((p) => !/^bot[1-9]$/i.test(p)),
    };
  }

  return { status: trimmed, lineup: [] };
}

/**
 * Commentaire défi par équipe (Java) : statut + composition
 * ex. « Début dans 6 minutes panurge2, panurge, patrick, X contre Nicoboy, JPG, beuz, Y »
 * (attaquant parfois vide → virgules orphelines)
 */
function parseTeamDefiComment(comm: string | undefined): {
  status: string;
  lineup1: string[];
  lineup2: string[];
} {
  if (!comm) return { status: '', lineup1: [], lineup2: [] };
  const text = stripConnectedCount(decodeHtmlEntities(comm))
    .replace(/\s+,/g, ',')
    .replace(/,\s*,+/g, ',')
    .replace(/\bcontre\s*,/gi, 'contre ')
    .replace(/,\s*contre\b/gi, ' contre ')
    .replace(/(^|\s),+/g, '$1')
    .replace(/,+\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const contreIdx = text.search(/\s+contre\s+/i);
  if (contreIdx >= 0) {
    const left = text.slice(0, contreIdx).trim();
    const right = text.slice(contreIdx).replace(/^\s*contre\s+/i, '').trim();
    const side1 = extractTrailingLineup(left);
    const side2 = extractTrailingLineup(right);
    const rightAsLineup = splitDefiPseudos(right);
    if (side1.lineup.length >= 3 && (side2.lineup.length >= 3 || rightAsLineup.length >= 3)) {
      return {
        status: side1.status,
        lineup1: side1.lineup,
        lineup2: side2.lineup.length >= 3 ? side2.lineup : rightAsLineup,
      };
    }
  }

  const solo = extractTrailingLineup(text);
  if (solo.lineup.length >= 3) {
    return { status: solo.status, lineup1: solo.lineup, lineup2: [] };
  }

  return { status: stripTrailingPseudoList(text), lineup1: [], lineup2: [] };
}

function isTeamDefiKind(kind: DefiKind | null | undefined, defiInfo: DefiInfo | null): boolean {
  if (kind === 'championnat_equipe' || kind === 'par_equipe' || kind === 'entrainement') return true;
  if (!defiInfo) return false;
  const t = defiInfo.type.toLowerCase();
  return t.includes('équipe') || t.includes('equipe');
}

/** Libellé contexte CIA : Barrage, ou nom de poule/division */
function getCiaContextLabel(table: GameTable): string | null {
  if (table.isBarrage || table.phaseEtat === 'barrage' || table.divisionType === 'playoff') {
    return 'Barrage';
  }
  if (table.divisionNom) {
    const nom = decodeHtmlEntities(table.divisionNom);
    const type = table.divisionType;
    if (type === 'groupe' && !/^poule\b/i.test(nom)) return `Poule ${nom}`;
    return nom;
  }
  return null;
}

/** Parse deal progress from comm text (e.g., "donne 5/12") */
function parseDealProgress(comm: string | undefined): { current: number; total: number } | null {
  if (!comm) return null;
  const decoded = decodeHtmlEntities(comm);
  const match = decoded.match(/donne\s*n?°?\s*(\d+)\s*\/\s*(\d+)/i);
  if (!match) return null;
  return { current: parseInt(match[1], 10), total: parseInt(match[2], 10) };
}

/** Check if current user is connected to a table */
function isUserInTable(table: GameTable, pseudo: string): boolean {
  if (!pseudo) return false;
  const normalizedPseudo = pseudo.toLowerCase();
  return (table.joueurs || []).some(j => {
    const name = Array.isArray(j) ? (j[0] || '') : (j.pseudo || '');
    return decodePseudo(name).toLowerCase() === normalizedPseudo
      || (j && !Array.isArray(j) && j.pseudo_display?.toLowerCase() === normalizedPseudo);
  });
}

/** Check if current user is involved in a draft table (creator, invited, offline, etc.) */
function isUserInDraftTable(table: GameTable, pseudo: string): boolean {
  const draftTable = table as GameTable & { _isDraft?: boolean; _memberPseudos?: string[] };
  if (!draftTable._isDraft || !pseudo) return false;
  const normalizedPseudo = pseudo.toLowerCase();
  if (draftTable._memberPseudos?.some((p) => p.toLowerCase() === normalizedPseudo)) {
    return true;
  }
  return isUserInTable(table, pseudo);
}

// Parse les infos d'un défi depuis nomtable et comm
// nomtable: "Défi libre : equipe1joueur1,equipe1joueur2 contre equipe2joueur1,equipe2joueur2"
// comm peut contenir le score: "Score : 25.625 - 32.874, Donne n°12/16"
export interface DefiInfo {
  type: string; // "Défi libre", "Défi permanent", etc.
  team1: string[];
  team2: string[];
  team1OnlyBots: boolean;
  team2OnlyBots: boolean;
  score1?: number;
  score2?: number;
  currentDeal?: number;
  totalDeals?: number;
}

export function parseDefiInfo(nomtable: string | undefined, comm: string | undefined): DefiInfo | null {
  if (!nomtable) return null;
  
  const decoded = decodeHtmlEntities(nomtable);
  
  // Extraire le type et les équipes
  // Format: "Type : joueur1,joueur2 contre joueur3,joueur4"
  // Format entraînement: "Défi par équipe : Entrainement de NomEquipe"
  const colonIndex = decoded.indexOf(':');
  if (colonIndex === -1) return null;
  
  const type = decoded.substring(0, colonIndex).trim();
  const teamsStr = decoded.substring(colonIndex + 1).trim();

  const parseTeam = (teamStr: string): string[] => {
    return teamStr
      .split(',')
      .map(p => decodePseudo(p.trim()))
      .filter(p => p && !p.match(/^bot[1-9]$/i)); // Filtrer les bots
  };

  const countBots = (teamStr: string): boolean => {
    const players = teamStr.split(',').map(p => decodePseudo(p.trim())).filter(Boolean);
    return players.length > 0 && players.every(p => p.match(/^bot[1-9]$/i));
  };

  const trainingMatch = teamsStr.match(/^Entra[iî]nement de\s+(.+)$/i);
  let team1: string[];
  let team2: string[];
  let team1OnlyBots: boolean;
  let team2OnlyBots: boolean;

  if (trainingMatch) {
    const teamName = decodePseudo(trainingMatch[1].trim());
    team1 = teamName ? [teamName] : [];
    team2 = [];
    team1OnlyBots = false;
    team2OnlyBots = true; // adversaire = bots
  } else {
    const contreParts = teamsStr.split(/\s+contre\s+/i);
    if (contreParts.length !== 2) {
      return null;
    }
    team1 = parseTeam(contreParts[0]);
    team2 = parseTeam(contreParts[1]);
    team1OnlyBots = countBots(contreParts[0]);
    team2OnlyBots = countBots(contreParts[1]);
  }
  
  // Extraire le score et la progression depuis comm si présent
  let score1: number | undefined;
  let score2: number | undefined;
  let currentDeal: number | undefined;
  let totalDeals: number | undefined;
  
  if (comm) {
    const decodedComm = decodeHtmlEntities(comm);
    
    // Pattern score: "X.XXX - Y.YYY" ou "X - Y"
    const scoreMatch = decodedComm.match(/([\d.,]+)\s*-\s*([\d.,]+)/);
    if (scoreMatch) {
      score1 = parseFloat(scoreMatch[1].replace(',', '.'));
      score2 = parseFloat(scoreMatch[2].replace(',', '.'));
    }
    
    // Pattern progression: "Donne n°X/Y" ou "Donne X/Y"
    const dealMatch = decodedComm.match(/[Dd]onne\s*n?°?\s*(\d+)\s*\/\s*(\d+)/);
    if (dealMatch) {
      currentDeal = parseInt(dealMatch[1], 10);
      totalDeals = parseInt(dealMatch[2], 10);
    }
  }
  
  return { type, team1, team2, team1OnlyBots, team2OnlyBots, score1, score2, currentDeal, totalDeals };
}

// Extrait le titre depuis nomtable
// Ex: "Différé Individuel 100% Attaque" -> "100% Attaque"
// Ex: "Serveur \"Live Compétition !\"" -> "Live !"
function extractTableTitle(nomtable: string | undefined, idserv: string): string {
  if (!nomtable) return '';
  
  const decoded = stripConnectedCount(decodeHtmlEntities(nomtable));
  
  if (idserv === 'diff_indiv' || idserv === 'diff_tq') {
    const match = decoded.match(/Différé\s+(?:Individuel\s+|Équipe\s+|Triplette\s+)?(.+)/i);
    if (match) return match[1];
    return decoded;
  }
  
  if (idserv === 'live') {
    const match = decoded.match(/(?:Serveur\s+)?["«]?Live[!]?\s*([^"»]*)["»]?/i);
    if (match) {
      const suffix = match[1].trim();
      return suffix ? `Live ! ${suffix}` : 'Live !';
    }
    if (decoded.toLowerCase().includes('live')) {
      return decoded.replace(/^Serveur\s+/i, '').replace(/["«»]/g, '').trim();
    }
    return 'Live !';
  }
  
  return decoded;
}

// Composant pour une table dans la liste (prévu pour usage futur)
export function TableItem({ 
  table, 
  isSelected,
  onSelect,
  onJoin,
}: { 
  table: GameTable; 
  isSelected: boolean;
  onSelect: () => void;
  onJoin: () => void;
}) {
  void onJoin; // Prévu pour usage futur
  const playerCount = table.joueurs?.length || 0;
  const maxPlayers = table.nbj || 4;
  const isFull = playerCount >= maxPlayers;
  
  // Titre selon le type - utiliser nomtable
  const isDefi = table.idserv === 'defis';
  const defiInfo = isDefi ? parseDefiInfo(table.nomtable, table.comm) : null;
  
  let title = '';
  let preview = '';
  
  if (table.idserv === 'diff_indiv' || table.idserv === 'diff_tq' || table.idserv === 'live') {
    title = extractTableTitle(table.nomtable, table.idserv);
  } else if (table.idserv === 'libre') {
    const firstPlayer = table.joueurs?.[0];
    if (firstPlayer && Array.isArray(firstPlayer) && firstPlayer[1]) {
      title = `Table de ${firstPlayer[1]}`;
    } else if (firstPlayer && !Array.isArray(firstPlayer)) {
      title = `Table de ${firstPlayer.pseudo_display || decodePseudo(firstPlayer.pseudo || '')}`;
    } else {
      title = 'Table libre';
    }
  } else if (isDefi && defiInfo) {
    title = defiInfo.type;
    const team1Preview = defiInfo.team1.slice(0, 2).join(', ') || '—';
    const team2Preview = defiInfo.team2.slice(0, 2).join(', ') || '—';
    preview = `${team1Preview} ⚔️ ${team2Preview}`;
  } else if (isDefi) {
    title = table.nomtable ? decodeHtmlEntities(table.nomtable).split(':')[0] : 'Défi';
  } else {
    title = table.nomtable ? decodeHtmlEntities(table.nomtable) : 'Table';
  }
  
  // Pour différé et live, jamais "complet"
  const isDiffereOrLive = table.idserv === 'diff_indiv' || table.idserv === 'diff_tq' || table.idserv === 'live';
  const actuallyFull = !isDiffereOrLive && !isDefi && isFull;
  
  return (
    <div 
      className={`conversation-item ${isSelected ? 'active' : ''} ${actuallyFull ? 'table-full' : ''}`}
      onClick={onSelect}
    >
      <div className="conversation-avatar">
        <span className="table-icon-avatar" style={{ color: getTableTypeColor(table.idserv) }}>{getTableIcon(table.idserv, 24)}</span>
      </div>
      <div className="conversation-content">
        <div className="conversation-header">
          <span className="conversation-name">{title}</span>
          {isDiffereOrLive ? (
            // Pour différé/live: badge avec format de jeu
            <span className="table-format-badge">à {maxPlayers}j</span>
          ) : (
            // Pour autres tables: X/Y joueurs
            <span className="conversation-time">{playerCount}/{maxPlayers}</span>
          )}
        </div>
        <div className="conversation-preview">
          {isDefi && preview ? preview : (
            table.comm ? decodeHtmlEntities(table.comm) : `${playerCount} joueur${playerCount > 1 ? 's' : ''}`
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tables({ session, isAdmin = false }: TablesProps) {
  const { startGame, gameInfo, activeTab, setActiveTab, playingUsers, setPlayingUsers, showToast, preferences, setTablesCategories, setJoinTable, setOpenRetroDiffere, expectedDefiTableKeys, removeExpectedDefiTable } = useApp();
  const { isCompact } = useResponsive();
  const [tables, setTables] = useState<TableList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  
  // Mode d'affichage : liste des tables ou éditeur de table brouillon
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [spectatorFilter, setSpectatorFilter] = useState<SpectatorMode | 'all'>('live');
  
  // Catégorie et table sélectionnées
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [retroDiffereOpen, setRetroDiffereOpen] = useState(false);
  useTabReselect('tables', () => {
    setShowMobileDetail(false);
    setRetroDiffereOpen(false);
    setViewMode((prev) => (prev === 'spectatorMultiplex' ? 'list' : prev));
  });
  const [_selectedTable, _setSelectedTable] = useState<GameTable | null>(null);
  void _selectedTable; void _setSelectedTable; // Prévu pour usage futur

  // Hook pour la table brouillon
  const draftHook = useDraftTable({
    socket,
    pseudo: session.pseudo,
    compet: session.compet,
  });

  // Sources Live regardables (pour le bouton "Spectateur" sur la catégorie Live)
  // Temporaire : mode spectateur/multiplex réservé aux administrateurs (pas encore de bêta-testeurs
  // pour le valider en conditions réelles). À retirer à l'ouverture de la bêta publique.
  const { sources: liveWatchableSources } = useSpectatorSources(socket, 'live', session.pseudo, session.compet && isAdmin);

  // Refs pour accéder aux valeurs dans les callbacks socket
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const prevGameInfoRef = useRef(gameInfo);
  const prevTableCountRef = useRef<number | null>(null);

  // Indique qu'on attend la réponse draft:created du serveur
  const [draftPending, setDraftPending] = useState(false);
  const [showDraftCloseConfirm, setShowDraftCloseConfirm] = useState(false);

  // Charger les tables depuis l'API
  const fetchTables = useCallback(async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tables?compet=${session.compet}`,
        {
          headers: {
            'Authorization': `Bearer ${session.token}`,
          },
        }
      );
      
      const data: TablesApiResponse = await response.json();
      
      if (data.success && data.data) {
        setTables(data.data.categories);
        prevTableCountRef.current = Object.values(data.data.categories)
          .reduce((sum, arr) => sum + arr.length, 0);
        setError(null);
        
        // Sélectionner la première catégorie non vide par défaut
        if (!selectedCategory) {
          const firstCategory = Object.entries(data.data.categories)
            .find(([_, tables]) => tables.length > 0);
          if (firstCategory) {
            setSelectedCategory(firstCategory[0]);
          }
        }
      } else {
        setError(data.error || 'Erreur lors du chargement des tables');
      }
    } catch (err) {
      console.error('Erreur fetch tables:', err);
      setError('Impossible de contacter le serveur');
    } finally {
      setLoading(false);
    }
  }, [session.compet, session.token, selectedCategory]);
  
  // Connexion Socket.IO pour les mises à jour en temps réel
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
    });
    
    newSocket.on('tables:update', (data: { compet: boolean; tables: TableList }) => {
      if (data.compet === session.compet) {
        const newTotal = Object.values(data.tables).reduce((sum, arr) => sum + arr.length, 0);
        const prev = prevTableCountRef.current;

        if (
          prev !== null &&
          newTotal > prev &&
          preferencesRef.current.notifyNewTables &&
          activeTabRef.current !== 'tables'
        ) {
          const diff = newTotal - prev;
          showToast({
            message: diff === 1 ? 'Nouvelle table disponible' : `${diff} nouvelles tables`,
            type: 'table',
            targetTab: 'tables',
            duration: 4000,
          }, { sourceTab: 'tables' });
        }
        prevTableCountRef.current = newTotal;

        setTables(data.tables);
      }
    });
    
    setSocket(newSocket);
    
    return () => {
      newSocket.disconnect();
    };
  }, [session.compet]);

  // Dériver playingUsers à partir de la liste des tables et pousser dans AppContext
  useEffect(() => {
    if (!tables) return;
    const playing = new Set<string>();
    for (const category of Object.values(tables)) {
      for (const table of category) {
        for (const joueur of table.joueurs) {
          if (joueur.pseudo) {
            playing.add(joueur.pseudo);
          }
        }
      }
    }
    setPlayingUsers(playing);
  }, [tables, setPlayingUsers]);

  // Nettoyer les clés "table de défi attendue" (surbrillance) quand la table
  // a disparu de la liste ou que l'utilisateur y est désormais connecté.
  useEffect(() => {
    if (!tables || expectedDefiTableKeys.size === 0) return;
    const defiTablesByKey = new Map<string, GameTable>();
    for (const category of Object.values(tables)) {
      for (const table of category) {
        if (table.idserv !== 'defis') continue;
        defiTablesByKey.set(`${table.ip}:${table.port}`, table);
      }
    }
    for (const key of expectedDefiTableKeys) {
      const table = defiTablesByKey.get(key);
      if (!table || isUserInTable(table, session.pseudo)) {
        removeExpectedDefiTable(key);
      }
    }
  }, [tables, expectedDefiTableKeys, removeExpectedDefiTable, session.pseudo]);

  // Quand le draft legacy est finalisé : le créateur ouvre la table tout de suite,
  // les autres attendent draft:legacy_join_now (créateur prêt + polling actif).
  useEffect(() => {
    const draft = draftHook.draft;
    if (draft?.status !== 'created' || !draft.finalizedTable) return;

    const isLegacyLibre =
      draft.config.tableType === 'legacy' && draft.config.gameMode === 'libre';
    const isCreator =
      draft.createdBy.toLowerCase() === session.pseudo.toLowerCase();

    if (isLegacyLibre && !isCreator) {
      showToast({
        type: 'info',
        message: 'Table créée — en attente que l\'hôte ouvre la partie…',
      });
      return;
    }

    const { ip, port, serverConnectionId, connectionId } = draft.finalizedTable;

    if (isLegacyLibre && isCreator) {
      startGame({
        ip,
        port,
        serverConnectionId,
        connectionId,
        idserv: 'libre',
        table: {
          idserv: 'libre',
          ip: ip || '',
          port: port || 0,
          type: 'libre',
          nbj: draft.config.nombreJoueurs,
          joueurs: [],
          modeEntree: draft.config.modeEntree,
        },
      });
    } else {
      startGame({
        ip,
        port,
        serverConnectionId,
        connectionId,
      });
    }

    draftHook.closeDraft();
    setViewMode('list');
  }, [
    draftHook.draft?.status,
    draftHook.draft?.finalizedTable,
    draftHook.draft?.config,
    draftHook.draft?.createdBy,
    session.pseudo,
    startGame,
    draftHook.closeDraft,
    showToast,
  ]);

  // Les joueurs invités rejoignent quand le créateur est prêt
  useEffect(() => {
    if (!socket) return;

    const LEGACY_JOIN_STAGGER_MS = 1800;

    const onLegacyJoinNow = (data: {
      draftId: string;
      modeEntree?: 1 | 2 | 3;
      joinOrder?: string[];
      table: { ip: string; port: number };
    }) => {
      const draft = draftHook.draft;
      if (!draft || draft.id !== data.draftId) return;
      if (draft.createdBy.toLowerCase() === session.pseudo.toLowerCase()) return;

      const { ip, port } = data.table;
      if (!ip || !port) {
        showToast({
          type: 'info',
          message: 'Impossible de trouver la table. Rejoignez-la depuis la liste.',
        });
        return;
      }

      const joinGame = () => {
        startGame({
          ip,
          port,
          idserv: 'libre',
          table: {
            idserv: 'libre',
            ip,
            port,
            type: 'libre',
            nbj: draft.config.nombreJoueurs,
            joueurs: [],
            modeEntree: data.modeEntree ?? draft.config.modeEntree,
          },
        });
        draftHook.closeDraft();
        setViewMode('list');
      };

      // Mode LIBRE / mot de passe : join séquentiel selon l'ordre des sièges
      const joinOrder = data.joinOrder ?? [];
      const myIndex = joinOrder.findIndex(
        (p) => p.toLowerCase() === session.pseudo.toLowerCase(),
      );

      if (data.modeEntree !== 2 && myIndex >= 0) {
        const delayMs = (myIndex + 1) * LEGACY_JOIN_STAGGER_MS;
        showToast({
          type: 'info',
          message: `Connexion à la table dans ${Math.round(delayMs / 1000)} s…`,
        });
        window.setTimeout(joinGame, delayMs);
      } else {
        joinGame();
      }
    };

    socket.on('draft:legacy_join_now', onLegacyJoinNow);
    return () => {
      socket.off('draft:legacy_join_now', onLegacyJoinNow);
    };
  }, [socket, draftHook.draft, session.pseudo, startGame, draftHook.closeDraft, showToast]);

  // Quand le draft est fermé (et qu'on n'est pas en attente de création), revenir au mode liste
  useEffect(() => {
    if (!draftHook.isInDraft && viewMode === 'draft' && !draftPending) {
      setViewMode('list');
    }
  }, [draftHook.isInDraft, viewMode, draftPending]);

  const draftPendingInviteeCount = useMemo(() => {
    if (!draftHook.draft) return 0;
    return draftHook.draft.players.filter(
      (p) => p.pseudo.toLowerCase() !== session.pseudo.toLowerCase()
        && (p.status === 'invited' || p.status === 'waiting_connect'),
    ).length;
  }, [draftHook.draft, session.pseudo]);

  const handleDraftEditorClose = useCallback(() => {
    if (draftPendingInviteeCount > 0) {
      setShowDraftCloseConfirm(true);
      return;
    }
    draftHook.closeDraft();
    setViewMode('list');
  }, [draftPendingInviteeCount, draftHook.closeDraft]);

  const handleDraftDetach = useCallback(() => {
    setShowDraftCloseConfirm(false);
    draftHook.detachDraft();
    setViewMode('list');
  }, [draftHook.detachDraft]);

  const handleDraftCancelPreparation = useCallback(() => {
    setShowDraftCloseConfirm(false);
    draftHook.closeDraft();
    setViewMode('list');
  }, [draftHook.closeDraft]);

  // Dès que le draft est reçu, on n'est plus en attente
  useEffect(() => {
    if (draftHook.isInDraft && draftPending) {
      setDraftPending(false);
    }
  }, [draftHook.isInDraft, draftPending]);

  // Si une erreur survient pendant l'attente, annuler et afficher l'erreur
  useEffect(() => {
    if (draftPending && draftHook.error) {
      setDraftPending(false);
      showToast({ type: 'info', message: `Erreur : ${draftHook.error}` });
      draftHook.clearError();
    }
  }, [draftPending, draftHook.error, draftHook.clearError, showToast]);

  // Timeout de sécurité : si le serveur ne répond pas en 8s, annuler l'attente
  useEffect(() => {
    if (!draftPending) return;
    const timer = setTimeout(() => {
      setDraftPending(false);
      showToast({ type: 'info', message: 'Le serveur ne répond pas. Réessayez.' });
    }, 8000);
    return () => clearTimeout(timer);
  }, [draftPending, showToast]);

  // Handler pour la finalisation : le backend demande au frontend de créer la table
  useEffect(() => {
    if (!socket) return;

    const onFinalizeCreate = async (data: {
      draftId: string;
      config: DraftTableConfig;
      players: Array<{ pseudo: string; status: string }>;
      createdBy?: string;
    }) => {
      console.log('[Tables] Finalisation draft -> création table', data);

      if (!session.hashPass) {
        socket.emit('draft:finalized', {
          draftId: data.draftId,
          success: false,
          error: 'hashPass manquant - veuillez vous reconnecter',
        });
        return;
      }

      try {
        if (data.config.gameMode === 'libre') {
          // Créer la table via game:create (même mécanisme que le bouton "+")
          socket.once('game:created', (response: {
            success: boolean;
            table?: GameTable;
            connectionId?: string;
            serverConnectionId?: string;
            error?: string;
          }) => {
            socket.emit('draft:finalized', {
              draftId: data.draftId,
              success: response.success,
              table: response.success ? {
                ip: response.table?.ip || '',
                port: response.table?.port || 0,
                connectionId: response.connectionId,
                serverConnectionId: response.serverConnectionId,
              } : undefined,
              error: response.error,
            });
          });

          socket.emit('game:create', {
            pseudo: session.pseudo,
            password: session.hashPass,
            nombre: data.config.nombreJoueurs,
            commentaire: data.config.commentaire || '',
            modeEntree: data.config.modeEntree,
            mdp: data.config.motDePasse || '',
            petitesAutorisees: data.config.petitesAutorisees,
            iaPeutPrendre: data.config.iaPeutPrendre,
            tableCachee: data.config.tableCachee,
          });
        } else {
          // Défi libre : à implémenter via webtarotAPI
          socket.emit('draft:finalized', {
            draftId: data.draftId,
            success: false,
            error: 'La création de défi libre via webtarotAPI n\'est pas encore implémentée',
          });
        }
      } catch (err) {
        socket.emit('draft:finalized', {
          draftId: data.draftId,
          success: false,
          error: err instanceof Error ? err.message : 'Erreur inattendue',
        });
      }
    };

    // Neo table finalization: backend created the engine, frontend transitions to game
    const onFinalizedNeo = (data: {
      draftId: string;
      tableId: string;
      config: unknown;
      spectators?: string[];
    }) => {
      console.log('[Tables] Neo table finalized', data);
      const isSpectator = data.spectators?.some(
        p => p.toLowerCase() === session.pseudo.toLowerCase(),
      );
      startGame({
        neo: true,
        neoTableId: data.tableId,
        spectatorMode: isSpectator,
        spectatorSourceId: isSpectator ? `neo:${data.tableId}` : undefined,
      });
      draftHook.closeDraft();
      setViewMode('list');
    };

    socket.on('draft:finalize_create', onFinalizeCreate);
    socket.on('draft:finalized_neo', onFinalizedNeo);
    return () => {
      socket.off('draft:finalize_create', onFinalizeCreate);
      socket.off('draft:finalized_neo', onFinalizedNeo);
    };
  }, [socket, session.pseudo, session.hashPass, startGame, draftHook]);

  // Écouter les demandes de création de draft depuis le Chat (bouton "Jouer" ou menu +)
  useEffect(() => {
    const handleCreateFromChat = (event: Event) => {
      const { participants } = (event as CustomEvent).detail as { participants: string[] };
      const humanParticipants = participants.filter((p) => !isBotPseudo(p));
      console.log('[Tables] Création draft depuis chat avec:', humanParticipants);
      
      if (gameInfo) {
        const confirmed = window.confirm(
          'Vous êtes déjà en train de jouer. Voulez-vous quitter votre table en cours ?'
        );
        if (!confirmed) return;
      }

      setDraftPending(true);
      draftHook.createDraft(
        { ia: session.compet ? 'rv' : 'delphi', spectateursAutorises: true, spectateurTempsReel: false },
        humanParticipants,
      );
      setViewMode('draft');
    };

    const handleGameInviteFromChat = (event: Event) => {
      const { participants } = (event as CustomEvent).detail as { participants: string[] };
      const humanParticipants = participants.filter((p) => !isBotPseudo(p));
      console.log('[Tables] Invitation jeu depuis chat avec:', humanParticipants);

      if (draftHook.isInDraft && draftHook.draft) {
        for (const pseudo of humanParticipants) {
          draftHook.invitePlayer(pseudo);
        }
        setActiveTab('tables');
        setViewMode('draft');
        showToast({
          message: 'Invitations envoyées à votre table en préparation',
          type: 'game',
          duration: 4000,
        });
        return;
      }

      if (gameInfo) {
        const confirmed = window.confirm(
          'Vous êtes déjà en train de jouer. Voulez-vous quitter votre table en cours pour inviter le groupe ?'
        );
        if (!confirmed) return;
      }

      setActiveTab('tables');
      setDraftPending(true);
      draftHook.createDraft(
        { ia: session.compet ? 'rv' : 'delphi', spectateursAutorises: true, spectateurTempsReel: false },
        humanParticipants,
      );
      setViewMode('draft');
    };

    window.addEventListener('create-draft-from-chat', handleCreateFromChat);
    window.addEventListener('game-invite-from-chat', handleGameInviteFromChat);
    return () => {
      window.removeEventListener('create-draft-from-chat', handleCreateFromChat);
      window.removeEventListener('game-invite-from-chat', handleGameInviteFromChat);
    };
  }, [gameInfo, session.compet, draftHook, setActiveTab, showToast]);

  // Écouter les acceptations d'invitation (le joueur invité rejoint la table brouillon)
  useEffect(() => {
    const handleJoinFromInvitation = (event: Event) => {
      const { draftId, gameMode } = (event as CustomEvent).detail as {
        draftId: string;
        gameMode?: string;
      };
      console.log('[Tables] Invitation acceptée, rejoindre draft:', draftId);

      const categoryKey = findCategoryKeyForIdserv(tables, idservForDraftGameMode(gameMode));
      if (categoryKey) {
        setSelectedCategory(categoryKey);
      }

      setActiveTab('tables');
      setShowMobileDetail(true);
      draftHook.joinDraft(draftId);
      setDraftPending(true);
      setViewMode('draft');
    };

    window.addEventListener('join-draft-from-invitation', handleJoinFromInvitation);
    return () => window.removeEventListener('join-draft-from-invitation', handleJoinFromInvitation);
  }, [draftHook.joinDraft, setActiveTab, tables]);

  // Charger les tables au montage
  useEffect(() => {
    fetchTables();
  }, [fetchTables]);
  
  // Handler pour rejoindre une table
  const handleJoinTable = useCallback((table: GameTable) => {
    // Si c'est une table brouillon (draft), la rejoindre
    const draftTable = table as GameTable & { _draftId?: string; _isDraft?: boolean };
    if (draftTable._isDraft && draftTable._draftId) {
      const categoryKey = findCategoryKeyForIdserv(
        tables,
        table.idserv === 'defis' ? 'defis' : 'libre',
      );
      if (categoryKey) {
        setSelectedCategory(categoryKey);
      }
      setShowMobileDetail(true);
      draftHook.joinDraft(draftTable._draftId);
      setDraftPending(true);
      setViewMode('draft');
      return;
    }

    // Défis privés (libre / CIA / équipe…) : uniquement si on y est inscrit ou attendu
    if (table.idserv === 'defis') {
      const defiInfo = parseDefiInfo(table.nomtable, table.comm);
      const defiKind = getTableDefiKind(table);
      const isOpenDefi = defiKind === 'ouvert'
        || Boolean(defiInfo && defiInfo.type.toLowerCase().includes('ouvert'));
      const isExpectedForMe = expectedDefiTableKeys.has(`${table.ip}:${table.port}`);
      if (!canAccessDefiTable({
        isDefi: true,
        isOpenDefi,
        defiInfo,
        defiKind,
        table,
        pseudo: session.pseudo,
        isExpectedForMe,
      })) {
        return;
      }
    }

    // Si c'est une table Neo, la rejoindre directement ou en spectateur
    const neoTable = table as GameTable & {
      neo?: boolean;
      tableId?: string;
      rejoindreEnCours?: boolean;
      nbSpectateurs?: number;
    };
    if (neoTable.neo && neoTable.tableId) {
      startGame({
        neo: true,
        neoTableId: neoTable.tableId,
      });
      return;
    }
    
    startGame({
      ip: table.ip,
      port: table.port,
      idserv: table.idserv,
    });
  }, [startGame, draftHook.joinDraft, tables, session.pseudo, expectedDefiTableKeys]);

  // Aligner la catégorie active sur le type de table brouillon ouverte
  useEffect(() => {
    if (viewMode !== 'draft' || !draftHook.draft) return;
    const categoryKey = findCategoryKeyForIdserv(
      tables,
      idservForDraftGameMode(draftHook.draft.config.gameMode),
    );
    if (categoryKey) {
      setSelectedCategory(categoryKey);
    }
  }, [viewMode, draftHook.draft, tables]);

  const handleWatchTable = useCallback((table: GameTable) => {
    const neoTable = table as GameTable & { neo?: boolean; tableId?: string };
    if (!neoTable.neo || !neoTable.tableId) return;
    startGame({
      neo: true,
      neoTableId: neoTable.tableId,
      spectatorMode: true,
      spectatorSourceId: `neo:${neoTable.tableId}`,
    });
  }, [startGame]);

  // Exposer handleJoinTable via le contexte (pour la page Home)
  useEffect(() => {
    setJoinTable(handleJoinTable);
    return () => setJoinTable(null);
  }, [handleJoinTable, setJoinTable]);

  // Handler pour créer une table brouillon (collaboratif)
  const handleCreateDraft = useCallback((gameMode: 'libre' | 'defi_libre' = 'libre') => {
    // Vérifier si le joueur est déjà en jeu
    if (gameInfo) {
      const confirm = window.confirm(
        'Vous êtes déjà en train de jouer. Voulez-vous quitter votre table en cours pour créer une nouvelle table ?'
      );
      if (!confirm) return;
    }

    const config: Partial<DraftTableConfig> = {
      gameMode,
      ia: session.compet ? 'rv' : 'delphi',
      spectateursAutorises: true,
      spectateurTempsReel: false,
    };

    setDraftPending(true);
    draftHook.createDraft(config);
    setViewMode('draft');
  }, [gameInfo, session.compet, draftHook.createDraft]);
  
  // Handler pour créer une table libre (4 joueurs)
  const handleCreateTable = useCallback(() => {
    if (!socket) {
      alert('Non connecté au serveur');
      return;
    }
    
    if (!session.hashPass) {
      alert('Session invalide (hashPass manquant). Veuillez vous déconnecter et vous reconnecter avec votre vrai mot de passe.');
      return;
    }
    
    console.log('[Tables] Création d\'une table libre à 4 joueurs');
    console.log('[Tables] hashPass présent:', session.hashPass.substring(0, 8) + '...');
    
    // Écouter la réponse
    socket.once('game:created', (response: { 
      success: boolean; 
      table?: GameTable; 
      connectionId?: string;
      serverConnectionId?: string;
      error?: string;
    }) => {
      console.log('[Tables] Réponse création table:', response);
      
      if (response.success && response.serverConnectionId) {
        // Démarrer la partie via le contexte (monte le composant Game)
        startGame({
          serverConnectionId: response.serverConnectionId,
          connectionId: response.connectionId,
          table: response.table,
        });
      } else {
        alert(response.error || 'Impossible de créer la table');
      }
    });
    
    // Envoyer la demande de création
    socket.emit('game:create', {
      pseudo: session.pseudo,
      password: session.hashPass || '',  // Le hashPass est déjà disponible
      nombre: 4,
      commentaire: '',
    });
  }, [socket, session.pseudo, session.hashPass, startGame]);
  
  // Catégories triées
  const sortedCategories = useMemo(() => {
    if (!tables) return [];
    
    const order = ['Live', 'Différé Individuel', 'Differes équipe et triplette', 'Tables libres', 'Défis'];
    
    return Object.entries(tables)
      .map(([name, items]) => ({
        name: decodeHtmlEntities(name),
        originalName: name,
        tables: items,
        playerCount: items.reduce((sum, t) => sum + (t.joueurs?.length || 0), 0),
      }))
      .sort((a, b) => {
        const aIndex = order.findIndex(o => a.name.toLowerCase().includes(o.toLowerCase()));
        const bIndex = order.findIndex(o => b.name.toLowerCase().includes(o.toLowerCase()));
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
  }, [tables]);

  // Synchroniser les catégories triées vers le contexte (pour la page Home)
  useEffect(() => {
    setTablesCategories(sortedCategories);
  }, [sortedCategories, setTablesCategories]);
  
  // Tables de la catégorie sélectionnée
  const selectedCategoryTables = useMemo(() => {
    if (!selectedCategory || !tables) return [];
    const list = tables[selectedCategory] || [];
    if (list.some((t) => t.idserv === 'defis')) {
      return sortDefiTables(list.map((t) => (
        t.idserv === 'defis' ? enrichGameTableDefiMeta(t) as GameTable : t
      )));
    }
    return list;
  }, [selectedCategory, tables]);
  
  // Précharger les avatars de tous les joueurs visibles dans la catégorie sélectionnée
  const visiblePseudos = useMemo(() => {
    const pseudos: string[] = [];
    for (const table of selectedCategoryTables) {
      if (table.idserv === 'defis' && table.nomtable) {
        const defi = parseDefiInfo(table.nomtable, table.comm);
        if (defi) {
          pseudos.push(...defi.team1, ...defi.team2);
          continue;
        }
      }
      for (const j of (table.joueurs || [])) {
        const name = Array.isArray(j) ? (j[0] || '') : (j.pseudo || '');
        if (name && !name.match(/^bot[1-9]$/i)) pseudos.push(name);
      }
    }
    return [...new Set(pseudos)];
  }, [selectedCategoryTables]);
  usePrefetchAvatars(visiblePseudos);

  // Statistiques totales
  const totalStats = useMemo(() => {
    if (!tables) return { tables: 0, players: 0 };
    let tableCount = 0;
    let playerCount = 0;
    for (const category of Object.values(tables)) {
      tableCount += category.length;
      for (const table of category) {
        playerCount += table.joueurs?.length || 0;
      }
    }
    return { tables: tableCount, players: playerCount };
  }, [tables]);

  const diffIndivCategoryKey = useMemo(() => {
    const cat = sortedCategories.find(c =>
      isDiffIndivCategoryName(c.name) || c.tables.some(t => t.idserv === 'diff_indiv'),
    );
    return cat?.originalName ?? null;
  }, [sortedCategories]);

  const openRetroDiffereView = useCallback(() => {
    if (diffIndivCategoryKey) {
      setSelectedCategory(diffIndivCategoryKey);
    }
    setRetroDiffereOpen(true);
    setViewMode('list');
    setShowMobileDetail(true);
  }, [diffIndivCategoryKey]);

  useEffect(() => {
    setOpenRetroDiffere(openRetroDiffereView);
    return () => setOpenRetroDiffere(null);
  }, [setOpenRetroDiffere, openRetroDiffereView]);

  useEffect(() => {
    if (prevGameInfoRef.current?.retroDiffere && !gameInfo) {
      openRetroDiffereView();
    }
    prevGameInfoRef.current = gameInfo;
  }, [gameInfo, openRetroDiffereView]);

  const isDiffIndivCategory = useMemo(() => {
    if (!selectedCategory) return false;
    const decoded = decodeHtmlEntities(selectedCategory).toLowerCase();
    if (decoded.includes('différé individuel') || decoded.includes('differe individuel')) return true;
    return selectedCategoryTables.some(t => t.idserv === 'diff_indiv');
  }, [selectedCategory, selectedCategoryTables]);

  const sidebarContent = (
    <>
      <div className="sidebar-header">
        <div className="sidebar-header-row">
          <h2 className="sidebar-title">Tables de jeu</h2>
          {session.compet && isAdmin && (
            <button
              type="button"
              className="create-table-button"
              onClick={() => { setSpectatorFilter('all'); setViewMode('spectatorMultiplex'); }}
              title="Multiplex spectateur (réservé aux admins pendant la bêta)"
            >
              <LiveBroadcastIcon size={18} />
            </button>
          )}
          <button 
            className="create-table-button"
            onClick={handleCreateTable}
            title="Créer une table libre (4 joueurs)"
          >
            <PlusIcon size={18} />
          </button>
        </div>
        <span className="tables-stats">
          {totalStats.players} joueur{totalStats.players > 1 ? 's' : ''}
        </span>
      </div>

        <div className="conversations-list">
          {loading && (
            <div className="tables-loading-inline">
              <div className="loading-spinner-small" />
              <span>Chargement...</span>
            </div>
          )}
          
          {error && (
            <div className="tables-error-inline">
              <span>{error}</span>
              <button onClick={fetchTables}>Réessayer</button>
            </div>
          )}
          
          {!loading && !error && (
            <>
              {/* Entrée "Toutes les tables" */}
              <div
                className={`conversation-item ${!isCompact && selectedCategory === ALL_TABLES_KEY ? 'active' : ''}`}
                onClick={() => {
                  setRetroDiffereOpen(false);
                  setSelectedCategory(ALL_TABLES_KEY);
                  setViewMode('list');
                  setShowMobileDetail(true);
                }}
              >
                <div className="conversation-avatar">
                  <span className="category-icon" style={{ color: 'var(--text-secondary)' }}><ListGridIcon size={24} /></span>
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <span className="conversation-name">Toutes les tables</span>
                    {totalStats.tables > 0 && (
                      <span className="table-count-badge">{totalStats.tables}</span>
                    )}
                  </div>
                  <div className="conversation-preview">
                    Vue d'ensemble
                  </div>
                </div>
              </div>

              {/* Catégories individuelles */}
              {sortedCategories.map(category => (
                <div
                  key={category.originalName}
                  className={`conversation-item ${!isCompact && selectedCategory === category.originalName ? 'active' : ''}`}
                  onClick={() => {
                    setRetroDiffereOpen(false);
                    setSelectedCategory(category.originalName);
                    setViewMode('list');
                    setShowMobileDetail(true);
                  }}
                >
                  <div className="conversation-avatar">
                    <span className="category-icon" style={{ color: getTableTypeColor(getCategoryIdserv(category.name)) }}>
                      {getTableIcon(getCategoryIdserv(category.name), 24)}
                    </span>
                  </div>
                  <div className="conversation-content">
                    <div className="conversation-header">
                      <span className="conversation-name">{category.name}</span>
                      {category.tables.length > 0 && (
                        <span className="table-count-badge">{category.tables.length}</span>
                      )}
                    </div>
                    <div className="conversation-preview">
                      {category.playerCount} joueur{category.playerCount > 1 ? 's' : ''} connecté{category.playerCount > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
    </>
  );

  const mainContent = (
    <main className="chat-main tables-main">
        {viewMode === 'spectatorMultiplex' ? (
          <SpectatorMultiplex
            socket={socket}
            pseudo={session.pseudo}
            hasCompetitionAccess={session.hasCompetitionAccess}
            onBack={() => setViewMode('list')}
            initialFilter={spectatorFilter}
          />
        ) : viewMode === 'draft' && draftPending && !draftHook.draft ? (
          /* Attente de la création du draft */
          <div className="tables-no-selection">
            <div className="no-selection-content">
              <div className="loading-spinner-small" />
              <h3>Création de la table...</h3>
            </div>
          </div>
        ) : viewMode === 'draft' && draftHook.draft ? (
          /* Mode éditeur de table brouillon */
          <DraftTableEditor
            draft={draftHook.draft}
            currentPseudo={session.pseudo}
            isAdmin={draftHook.isAdmin}
            compet={session.compet}
            token={session.token || ''}
            onUpdateConfig={draftHook.updateConfig}
            onUpdateSeats={draftHook.updateSeats}
            onInvitePlayer={draftHook.invitePlayer}
            onKickPlayer={draftHook.kickPlayer}
            onSendMessage={draftHook.sendMessage}
            onSwitchMode={draftHook.switchMode}
            onFinalize={draftHook.finalize}
            onClose={handleDraftEditorClose}
            playingUsers={playingUsers}
          />
        ) : retroDiffereOpen && session.compet && isDiffIndivCategory ? (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h2>Différé Rétro</h2>
                <span className="chat-subtitle">Rejouez un différé historique contre 3 bots RV</span>
              </div>
            </div>
            <RetroDifferePage
              session={session}
              onBack={() => setRetroDiffereOpen(false)}
            />
          </>
        ) : selectedCategory === ALL_TABLES_KEY ? (
          /* Mode "Toutes les tables" - vue compacte en liste */
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h2>Toutes les tables</h2>
                <span className="chat-subtitle">
                  {totalStats.tables} table{totalStats.tables > 1 ? 's' : ''} · {totalStats.players} joueur{totalStats.players > 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <AllTablesList
              sortedCategories={sortedCategories}
              onJoinTable={handleJoinTable}
              vintageMode={preferences.vintageTableIcons}
              currentPseudo={session.pseudo}
              onSelectCategory={(categoryKey) => {
                setRetroDiffereOpen(false);
                setSelectedCategory(categoryKey);
                setViewMode('list');
              }}
              compet={session.compet}
              onOpenRetroDiffere={openRetroDiffereView}
            />
          </>
        ) : selectedCategory ? (
          /* Mode liste des tables (cartes) */
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h2>{decodeHtmlEntities(selectedCategory)}</h2>
                <span className="chat-subtitle">
                  {selectedCategoryTables.length} table{selectedCategoryTables.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="table-creation-buttons">
                {session.compet && isAdmin && selectedCategoryTables.some(t => t.idserv === 'live') && liveWatchableSources.length > 0 && (
                  <button
                    className="create-draft-btn spectator-category-btn"
                    onClick={() => { setSpectatorFilter('live'); setViewMode('spectatorMultiplex'); }}
                    title="Regarder une partie Live en différé"
                  >
                    <LiveBroadcastIcon size={16} /> Spectateur
                  </button>
                )}
                {(selectedCategoryTables.some(t => t.idserv === 'libre')
                  || decodeHtmlEntities(selectedCategory).toLowerCase().includes('libre')) && (
                  <button
                    className="create-draft-btn"
                    onClick={() => handleCreateDraft('libre')}
                    title="Créer une table libre en mode collaboratif"
                  >
                    Créer une table
                  </button>
                )}
                {(selectedCategoryTables.some(t => t.idserv === 'defis')
                  || decodeHtmlEntities(selectedCategory).toLowerCase().includes('défi')) && session.compet && (
                  <button
                    className="create-draft-btn"
                    onClick={() => handleCreateDraft('defi_libre')}
                    title="Créer un défi libre"
                  >
                    Défi libre
                  </button>
                )}
              </div>
            </div>
            
            <div className="tables-grid">
              {session.compet && isDiffIndivCategory && (
                <RetroDiffereTableCard pseudo={session.pseudo} onOpen={openRetroDiffereView} />
              )}
              {selectedCategoryTables.length === 0 && !(session.compet && isDiffIndivCategory) ? (
                <div className="tables-empty-category">
                  <p>Aucune table dans cette catégorie</p>
                </div>
              ) : (
                selectedCategoryTables.map((table, idx) => {
                  const prev = idx > 0 ? selectedCategoryTables[idx - 1] : null;
                  const showDefiSection = table.idserv === 'defis'
                    && (!prev || prev.idserv !== 'defis' || getTableDefiKind(prev) !== getTableDefiKind(table));
                  return (
                    <React.Fragment key={`${table.ip}-${table.port}-${idx}`}>
                      {showDefiSection && (
                        <div
                          className="tables-defi-section-header"
                          style={{ color: getDefiKindColor(getTableDefiKind(table)) }}
                        >
                          {getTableDefiLabel(table)}
                        </div>
                      )}
                      <TableCard
                        table={table}
                        onJoin={() => handleJoinTable(table)}
                        onWatch={() => handleWatchTable(table)}
                        currentPseudo={session.pseudo}
                      />
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="tables-no-selection">
            <div className="no-selection-content">
              <span className="no-selection-icon">🃏</span>
              <h3>Sélectionnez une catégorie</h3>
              <p>Choisissez une catégorie de tables dans la liste à gauche</p>
            </div>
          </div>
        )}
      </main>
  );

  const categoryName = viewMode === 'draft' && draftHook.draft
    ? (draftHook.draft.config.gameMode === 'defi_libre'
      ? 'Défi libre en préparation'
      : 'Table libre en préparation')
    : selectedCategory === ALL_TABLES_KEY
    ? 'Toutes les tables'
    : sortedCategories.find(c => c.originalName === selectedCategory)?.name || 'Tables';

  const categoryCount = viewMode === 'draft' && draftHook.draft
    ? `${draftHook.draft.players.filter(p => p.status === 'joined').length} joueur${
      draftHook.draft.players.filter(p => p.status === 'joined').length > 1 ? 's' : ''
    }`
    : selectedCategory === ALL_TABLES_KEY
    ? `${totalStats.tables} table${totalStats.tables > 1 ? 's' : ''}`
    : selectedCategory
      ? `${selectedCategoryTables.length} table${selectedCategoryTables.length > 1 ? 's' : ''}`
      : '';

  const tablesTitleContent = (
    <>
      <div className="back-title-info">
        <span className="back-title-name">{categoryName}</span>
        {categoryCount && <span className="back-title-subtitle">{categoryCount}</span>}
      </div>
    </>
  );

  const isLibreCategory = selectedCategory && (
    selectedCategoryTables.some(t => t.idserv === 'libre')
    || decodeHtmlEntities(selectedCategory).toLowerCase().includes('libre')
  );
  const isDefiCategory = selectedCategory && session.compet && (
    selectedCategoryTables.some(t => t.idserv === 'defis')
    || decodeHtmlEntities(selectedCategory).toLowerCase().includes('défi')
  );

  const compactActions = selectedCategory && selectedCategory !== ALL_TABLES_KEY ? (
    <>
      {isLibreCategory && (
        <button
          className="create-draft-btn compact"
          onClick={() => handleCreateDraft('libre')}
          title="Créer une table"
        >
          <PlusIcon size={16} />
        </button>
      )}
      {isDefiCategory && (
        <button
          className="create-draft-btn compact"
          onClick={() => handleCreateDraft('defi_libre')}
          title="Créer un défi"
        >
          ⚔️
        </button>
      )}
    </>
  ) : null;

  return (
    <div className="chat-container">
      {showDraftCloseConfirm && (
        <DraftCloseConfirm
          pendingCount={draftPendingInviteeCount}
          onKeepPreparing={handleDraftDetach}
          onCancelPreparation={handleDraftCancelPreparation}
        />
      )}
      <MasterDetail
        master={sidebarContent}
        detail={mainContent}
        detailTitle={tablesTitleContent}
        detailHeaderRight={compactActions}
        hasSelection={showMobileDetail}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}

/** Status indicator for a table row (modernized colored dots) */
export function getTableStatusInfo(table: GameTable): { color: string; label: string; pulse?: boolean } {
  const playerCount = table.joueurs?.length || 0;
  const isDiffere = table.idserv === 'diff_indiv' || table.idserv === 'diff_tq';
  const isDefi = table.idserv === 'defis';

  if (table.idserv === 'live') {
    return playerCount > 0
      ? { color: '#22c55e', label: 'Partie en cours', pulse: true }
      : { color: '#3b82f6', label: 'Table disponible' };
  }
  if (isDiffere) {
    return playerCount > 0
      ? { color: '#f59e0b', label: `${playerCount} joueur${playerCount > 1 ? 's' : ''} connecté${playerCount > 1 ? 's' : ''}` }
      : { color: '#3b82f6', label: 'Table ouverte, aucun joueur connecté' };
  }
  if (isDefi) {
    const defi = parseDefiInfo(table.nomtable, table.comm);
    if (defi?.currentDeal && defi?.totalDeals) {
      return { color: '#22c55e', label: `En cours — donne ${defi.currentDeal}/${defi.totalDeals}`, pulse: true };
    }
    return { color: '#f59e0b', label: 'Défi en cours' };
  }
  const maxPlayers = table.nbj || 4;
  if (playerCount >= maxPlayers) {
    return { color: '#6b7280', label: `Table complète (${playerCount}/${maxPlayers})` };
  }
  if (playerCount > 0) {
    return { color: '#22c55e', label: `${playerCount}/${maxPlayers} places occupées` };
  }
  return { color: '#3b82f6', label: 'Table vide, en attente de joueurs' };
}

/**
 * Legacy-style SVG circle seat indicator.
 * Circle divided into N slices (N = number of players).
 * Red = occupied seat, blue = empty seat.
 * For 4 players: slices rotated 45° so divisions are NE/SE/SW/NW (slices centered at N/E/S/W).
 * For différé/live: show 0 filled (no one) or 1 filled (someone connected), never more.
 */
function VintageSeatIcon({ nbj, connectedCount, isDiffereOrLive }: {
  nbj: number; connectedCount: number; isDiffereOrLive: boolean;
}) {
  const size = 18;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 1;
  const n = Math.max(3, Math.min(5, nbj));
  const effectiveConnected = isDiffereOrLive
    ? (connectedCount > 0 ? 1 : 0)
    : Math.min(connectedCount, n);

  const rotationOffset = -Math.PI / n;

  const slices: React.ReactElement[] = [];
  for (let i = 0; i < n; i++) {
    const startAngle = (2 * Math.PI * i) / n + Math.PI / 2 + rotationOffset;
    const endAngle = (2 * Math.PI * (i + 1)) / n + Math.PI / 2 + rotationOffset;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const filled = i < effectiveConnected;
    const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
    slices.push(
      <path key={i} d={d}
        fill={filled ? '#dc2626' : '#3b82f6'}
        stroke="var(--bg-secondary)" strokeWidth="0.8"
      />
    );
  }

  const label = isDiffereOrLive
    ? (connectedCount > 0 ? `${connectedCount} connecté${connectedCount > 1 ? 's' : ''}` : 'Aucun connecté')
    : `${effectiveConnected}/${n} places occupées`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="vintage-seat-icon"
      aria-label={label}>
      {slices}
    </svg>
  );
}

/**
 * Legacy-style game status cube (modernized).
 * Gray for différé (always accessible). Live/défi: colored based on game state.
 */
function VintageStatusCube({ table }: { table: GameTable }) {
  const isDiffere = table.idserv === 'diff_indiv' || table.idserv === 'diff_tq';
  const isDefi = table.idserv === 'defis';
  const defiInfo = isDefi ? parseDefiInfo(table.nomtable, table.comm) : null;
  const playerCount = table.joueurs?.length || 0;

  let color = '#6b7280';
  let trickNum: number | null = null;
  let label = 'Aucune partie en cours';

  if (isDiffere) {
    color = getTableTypeColor(table.idserv);
    label = 'Table différée — toujours accessible';
  } else if (defiInfo?.currentDeal && defiInfo?.totalDeals) {
    if (defiInfo.currentDeal >= defiInfo.totalDeals) {
      color = '#dc2626';
      label = 'Défi terminé';
    } else {
      color = '#22c55e';
      trickNum = defiInfo.currentDeal;
      label = `Donne ${defiInfo.currentDeal}/${defiInfo.totalDeals}`;
    }
  } else if (table.idserv === 'live' && playerCount > 0) {
    const donneMatch = table.comm ? decodeHtmlEntities(table.comm).match(/donne\s*(\d+)/i) : null;
    if (donneMatch) {
      color = '#22c55e';
      trickNum = parseInt(donneMatch[1], 10);
      label = `Donne ${trickNum} en cours`;
    } else {
      color = '#f59e0b';
      label = 'Partie vient de commencer';
    }
  } else if (isDefi) {
    color = '#f59e0b';
    label = 'Défi en cours';
  } else if (table.idserv === 'libre' && playerCount > 0) {
    color = '#f59e0b';
    label = 'Table en attente';
  }

  return (
    <span className="vintage-status-cube" style={{ background: color }} aria-label={label}>
      {trickNum !== null && <span className="vintage-cube-num">{trickNum}</span>}
    </span>
  );
}

/** Tooltip wrapper — positioned to the left of trigger, clamped within viewport */
function TableTooltip({ children, content }: { children: React.ReactNode; content: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; placement: 'above' | 'below' }>({ x: 0, y: 0, placement: 'above' });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const show = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceAbove = rect.top;
        if (spaceAbove < 120) {
          setPos({ x: rect.left, y: rect.bottom + 4, placement: 'below' });
        } else {
          setPos({ x: rect.left, y: rect.top - 4, placement: 'above' });
        }
      }
      setVisible(true);
    }, 300);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setVisible(false), 100);
  }, []);

  const transformStyle = pos.placement === 'below'
    ? 'translate(-100%, 0)'
    : 'translate(-100%, -100%)';

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className="tooltip-trigger"
      >
        {children}
      </span>
      {visible && (
        <div
          ref={tooltipRef}
          className="table-tooltip"
          style={{ left: pos.x, top: pos.y, transform: transformStyle }}
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={hide}
        >
          <div className="table-tooltip-content">{content}</div>
        </div>
      )}
    </>
  );
}

/** Build player tooltip with avatars and decoded pseudos */
function PlayerListTooltip({ names }: { names: string[] }) {
  if (names.length === 0) return <span className="status-tooltip-text">Aucun joueur connecté</span>;
  return (
    <div className="player-tooltip-list">
      {names.slice(0, 10).map((name, i) => (
        <div key={i} className="player-tooltip-row">
          <PlayerAvatar pseudo={name} size={18} className="player-tooltip-avatar" />
          <span>{decodeHtmlEntities(name)}</span>
        </div>
      ))}
      {names.length > 10 && (
        <div className="player-tooltip-more">+{names.length - 10} autres</div>
      )}
    </div>
  );
}

/**
 * Connected players display for live/différé.
 * 0 → "0 connecté", 1 → avatar+pseudo, 2-5 → avatars only (tooltip), 5+ → "xx connectés" (tooltip).
 */
function ConnectedPlayersCell({ names }: { names: string[] }) {
  const count = names.length;

  if (count === 0) {
    return null;
  }
  if (count === 1) {
    return (
      <span className="all-tables-connected-single">
        <PlayerAvatar pseudo={names[0]} size={16} className="all-tables-connected-avatar" />
        <span className="all-tables-connected-name">{decodeHtmlEntities(names[0])}</span>
      </span>
    );
  }
  if (count <= 5) {
    return (
      <TableTooltip content={<PlayerListTooltip names={names} />}>
        <span className="all-tables-connected-avatars">
          {names.map((n, i) => (
            <PlayerAvatar key={i} pseudo={n} size={16} className="all-tables-connected-avatar" />
          ))}
        </span>
      </TableTooltip>
    );
  }
  return (
    <TableTooltip content={<PlayerListTooltip names={names} />}>
      <span className="all-tables-row-players">{count} connectés</span>
    </TableTooltip>
  );
}

/**
 * Adaptive player display for libre/defi tables.
 * Shows avatars+names when space allows, avatars only if tight, with full tooltip always.
 */
function AdaptivePlayerList({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <TableTooltip content={<PlayerListTooltip names={names} />}>
      <span className="all-tables-adaptive-players">
        {names.slice(0, 5).map((name, i) => (
          <span key={i} className="all-tables-adaptive-player">
            <PlayerAvatar pseudo={name} size={16} className="all-tables-adaptive-avatar" />
            <span className="all-tables-adaptive-name">{decodeHtmlEntities(name)}</span>
          </span>
        ))}
        {names.length > 5 && (
          <span className="all-tables-adaptive-more">+{names.length - 5}</span>
        )}
      </span>
    </TableTooltip>
  );
}

/** Compact row for différé rétro in the all-tables list */
function AllTablesRetroRow({
  onOpen,
  vintageMode,
  pseudo,
}: {
  onOpen: () => void;
  vintageMode: boolean;
  pseudo: string;
}) {
  const { activePlayerCount, activePlayers, secondaryInfo } = useRetroDiffereTableInfo(pseudo, !!pseudo);
  const maxPlayers = 4;
  const isDiffereOrLive = true;
  const seatTooltipLabel = activePlayerCount > 0
    ? `${activePlayerCount} joueur${activePlayerCount > 1 ? 's' : ''} actif${activePlayerCount > 1 ? 's' : ''}`
    : 'Aucun joueur actif';

  return (
    <div className="all-tables-row clickable retro-differe-all-tables-row" onClick={onOpen}>
      {vintageMode ? (
        <TableTooltip content={
          <div className="vintage-tooltip">
            <div className="vintage-tooltip-row">{seatTooltipLabel}</div>
          </div>
        }>
          <span className="all-tables-vintage-indicators">
            <VintageSeatIcon nbj={maxPlayers} connectedCount={activePlayerCount} isDiffereOrLive={isDiffereOrLive} />
            <span className="vintage-status-cube" style={{ visibility: 'hidden' }} />
          </span>
        </TableTooltip>
      ) : (
        <TableTooltip content={<span className="status-tooltip-text">Table différée — toujours accessible</span>}>
          <span
            className="all-tables-status-dot"
            style={{ '--dot-color': '#3b82f6' } as React.CSSProperties}
          />
        </TableTooltip>
      )}
      <span className="all-tables-fft-badge" title="Table FFT">FFT</span>
      <span className="differe-subtype-icon" style={{ color: getTableTypeColor('diff_indiv') }}>
        <AttackIcon size={14} />
      </span>
      <span className="all-tables-row-name">Rétro classique</span>
      <span className="all-tables-row-right">
        <span className="all-tables-format-badge">à 4j</span>
        {secondaryInfo && (
          <span className="all-tables-row-info">{secondaryInfo}</span>
        )}
        {activePlayers.length > 0 && (
          <ConnectedPlayersCell names={activePlayers} />
        )}
      </span>
    </div>
  );
}

export function isDiffIndivCategoryName(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('différé individuel') || n.includes('differe individuel');
}

/** Compact list view of all tables grouped by category (legacy-inspired) */
export function AllTablesList({
  sortedCategories,
  onJoinTable,
  vintageMode,
  currentPseudo,
  onSelectCategory,
  compet,
  onOpenRetroDiffere,
}: {
  sortedCategories: Array<{ name: string; originalName: string; tables: GameTable[]; playerCount: number }>;
  onJoinTable: (table: GameTable) => void;
  vintageMode: boolean;
  currentPseudo: string;
  onSelectCategory: (categoryName: string) => void;
  compet?: boolean;
  onOpenRetroDiffere?: () => void;
}) {
  const hasRetroEntry = !!(compet && onOpenRetroDiffere && sortedCategories.some(c =>
    isDiffIndivCategoryName(c.name) || c.tables.some(t => t.idserv === 'diff_indiv'),
  ));
  const retroInfo = useRetroDiffereTableInfo(currentPseudo, !!(compet && hasRetroEntry));
  const totalTables = sortedCategories.reduce((sum, c) => sum + c.tables.length, 0) + (hasRetroEntry ? 1 : 0);

  if (totalTables === 0) {
    return (
      <div className="tables-no-selection">
        <div className="no-selection-content">
          <span className="no-selection-icon">🃏</span>
          <h3>Aucune table disponible</h3>
          <p>Il n'y a pas de tables en cours pour le moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="all-tables-list">
      {sortedCategories.map(category => {
        const showRetro = compet && onOpenRetroDiffere && (
          isDiffIndivCategoryName(category.name)
          || category.tables.some(t => t.idserv === 'diff_indiv')
        );
        if (category.tables.length === 0 && !showRetro) return null;

        const catIdserv = getCategoryIdserv(category.name);

        const catPlayerCount = category.playerCount + (showRetro ? retroInfo.activePlayerCount : 0);

        return (
          <div key={category.originalName} className="all-tables-group">
            <div
              className="all-tables-group-header clickable-header"
              onClick={() => onSelectCategory(category.originalName)}
              title={`Voir la vue détaillée « ${category.name} »`}
            >
              <span className="all-tables-group-icon" style={{ color: getTableTypeColor(catIdserv) }}>{getTableIcon(catIdserv, 16)}</span>
              <span className="all-tables-group-name">{category.name}</span>
              <span className="all-tables-group-count">{category.tables.length + (showRetro ? 1 : 0)}</span>
              <span className="all-tables-group-players">
                {catPlayerCount} joueur{catPlayerCount > 1 ? 's' : ''}
              </span>
            </div>

            <div className="all-tables-rows">
              {showRetro && (
                <AllTablesRetroRow
                  onOpen={onOpenRetroDiffere}
                  vintageMode={vintageMode}
                  pseudo={currentPseudo}
                />
              )}
              {category.tables.map((table, idx) => (
                <AllTablesRow
                  key={`${table.ip}-${table.port}-${idx}`}
                  table={table}
                  onJoin={() => onJoinTable(table)}
                  vintageMode={vintageMode}
                  currentPseudo={currentPseudo}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Build a list of decoded pseudo names from a table (with HTML entity decoding) */
export function getTablePlayerNames(table: GameTable): string[] {
  return (table.joueurs || []).map(j => {
    const raw = Array.isArray(j)
      ? (j[1] || j[0])
      : (j.pseudo_display || decodePseudo(j.pseudo || ''));
    return raw ? decodeHtmlEntities(raw) : '';
  }).filter(Boolean);
}

/** True if pseudo appears in a list of names (case-insensitive). */
function pseudoInList(names: string[] | undefined, pseudo: string): boolean {
  if (!names?.length || !pseudo) return false;
  const normalized = pseudo.toLowerCase();
  return names.some((p) => p.toLowerCase() === normalized);
}

/** Check if a user pseudo appears in a defi's player list (nomtable). */
export function isUserInDefi(defiInfo: DefiInfo | null, pseudo: string): boolean {
  if (!defiInfo) return false;
  return pseudoInList([...defiInfo.team1, ...defiInfo.team2], pseudo);
}

/**
 * Whether the current user may join a défi table from the UI.
 * Open défis are public; otherwise the user must be listed (nomtable / lineup)
 * or explicitly expected (server alert), or already connected.
 */
export function canAccessDefiTable(params: {
  isDefi: boolean;
  isDraft?: boolean;
  isOpenDefi: boolean;
  defiInfo: DefiInfo | null;
  defiKind: DefiKind | null;
  table: GameTable;
  pseudo: string;
  isExpectedForMe?: boolean;
}): boolean {
  const {
    isDefi,
    isDraft = false,
    isOpenDefi,
    defiInfo,
    defiKind,
    table,
    pseudo,
    isExpectedForMe = false,
  } = params;
  if (!isDefi || isDraft || isOpenDefi) return true;
  if (isExpectedForMe) return true;
  if (isUserInTable(table, pseudo)) return true;
  if (isUserInDefi(defiInfo, pseudo)) return true;
  if (isTeamDefiKind(defiKind, defiInfo)) {
    const lineup = parseTeamDefiComment(table.comm);
    if (pseudoInList([...lineup.lineup1, ...lineup.lineup2], pseudo)) return true;
  }
  return false;
}

/** A single compact row for a table */
export function AllTablesRow({ table, onJoin, vintageMode, currentPseudo }: {
  table: GameTable; onJoin: () => void; vintageMode: boolean; currentPseudo: string;
}) {
  const { expectedDefiTableKeys } = useApp();
  const draftTable = table as GameTable & { _isDraft?: boolean };
  const isDraft = draftTable._isDraft === true;
  const playerCount = table.joueurs?.length || 0;
  const maxPlayers = table.nbj || 4;
  const isDiffere = table.idserv === 'diff_indiv' || table.idserv === 'diff_tq';
  const isLive = table.idserv === 'live';
  const isDiffereOrLive = isDiffere || isLive;
  const isDefi = table.idserv === 'defis' && !isDraft;
  const isFull = !isDiffereOrLive && !isDefi && !isDraft && playerCount >= maxPlayers;
  const status = getTableStatusInfo(table);
  const defiInfo = isDefi ? parseDefiInfo(table.nomtable, table.comm) : null;
  const defiKind = isDefi ? getTableDefiKind(table) : null;
  const isTeamDefi = isTeamDefiKind(defiKind, defiInfo);
  const isCiaDefi = defiKind === 'cia';
  const isOpenDefi = defiKind === 'ouvert'
    || Boolean(defiInfo && defiInfo.type.toLowerCase().includes('ouvert'));
  const allPlayerNames = getTablePlayerNames(table);
  const teamDefiParsed = isTeamDefi ? parseTeamDefiComment(table.comm) : null;

  // Build display name
  let displayName = '';
  if (isDraft) {
    displayName = table.nomtable ? decodeHtmlEntities(table.nomtable) : 'Table en préparation';
  } else if (isDiffereOrLive) {
    displayName = extractTableTitle(table.nomtable, table.idserv);
  } else if (table.idserv === 'libre') {
    const first = table.joueurs?.[0];
    if (first && Array.isArray(first) && first[1]) {
      displayName = `Table de ${first[1]}`;
    } else if (first && !Array.isArray(first)) {
      displayName = `Table de ${first.pseudo_display || decodePseudo(first.pseudo || '')}`;
    } else {
      displayName = 'Table libre';
    }
  } else if (isDefi) {
    displayName = getTableDefiLabel(table);
  } else {
    displayName = table.nomtable ? decodeHtmlEntities(table.nomtable) : 'Table';
  }

  // Is current user connected to this table?
  const userIsConnected = isUserInTable(table, currentPseudo);
  const isMyDraft = isDraft && isUserInDraftTable(table, currentPseudo);

  // A legacy défi table (CIA / défi libre / défi par équipe) where the current
  // user is personally expected but hasn't joined yet (see defi-table-alerts.ts).
  const isExpectedForMe = isDefi && !userIsConnected
    && expectedDefiTableKeys.has(`${table.ip}:${table.port}`);

  const defiAccessible = canAccessDefiTable({
    isDefi,
    isDraft,
    isOpenDefi,
    defiInfo,
    defiKind,
    table,
    pseudo: currentPseudo,
    isExpectedForMe,
  });
  const defiFiltered = isDefi && !isDraft && !defiAccessible;

  // Build secondary info (strip redundant connected count)
  let secondaryInfo = '';
  let tournamentHighlight: { before: string; minutes: string } | null = null;
  if (isDraft) {
    secondaryInfo = `${playerCount}/${maxPlayers} joueurs`;
  } else if (isCiaDefi) {
    // Badge = poule/barrage ; secondary = statut / score (comme défi libre)
    secondaryInfo = stripTrailingPseudoList(stripConnectedCount(
      table.comm ? decodeHtmlEntities(table.comm) : '',
    ));
    if (!secondaryInfo && defiInfo?.score1 !== undefined) {
      secondaryInfo = `Score : ${defiInfo.score1?.toLocaleString('fr-FR')} - ${defiInfo.score2?.toLocaleString('fr-FR')}`;
    }
  } else if (isTeamDefi) {
    // Liste : pas de composition joueurs — uniquement statut / score
    secondaryInfo = teamDefiParsed?.status || '';
    if (!secondaryInfo && defiInfo?.score1 !== undefined) {
      secondaryInfo = `Score : ${defiInfo.score1?.toLocaleString('fr-FR')} - ${defiInfo.score2?.toLocaleString('fr-FR')}`;
      if (defiInfo.currentDeal && defiInfo.totalDeals) {
        secondaryInfo += ` · Donne ${defiInfo.currentDeal}/${defiInfo.totalDeals}`;
      }
    }
  } else if (isDefi && defiInfo?.score1 !== undefined) {
    secondaryInfo = `Score : ${defiInfo.score1?.toLocaleString('fr-FR')} - ${defiInfo.score2?.toLocaleString('fr-FR')}`;
    if (defiInfo.currentDeal && defiInfo.totalDeals) {
      secondaryInfo += ` · Donne ${defiInfo.currentDeal}/${defiInfo.totalDeals}`;
    }
  } else if (table.comm) {
    secondaryInfo = stripTrailingPseudoList(stripConnectedCount(decodeHtmlEntities(table.comm)));
  }

  // Detect live tournament announcement: "tournoi [xxx] dans moins de X minutes"
  const isLiveTournament = isLive && secondaryInfo && /tournoi\s+.*dans\s+moins\s+de\s+\d+\s+minute/i.test(secondaryInfo);
  if (isLiveTournament) {
    const match = secondaryInfo.match(/(.*?)(dans\s+moins\s+de\s+)(\d+\s+minute\S*)(.*)/i);
    if (match) {
      tournamentHighlight = {
        before: match[1] + match[2],
        minutes: match[3],
      };
    }
  }

  // Defi avatars : équipes (championnat) ou joueurs (CIA / libre)
  const defiAvatars = isDefi ? (() => {
    if (isTeamDefi && (table.team1?.nom || table.team2?.nom || defiInfo)) {
      const t1 = table.team1?.nom
        ? [table.team1.nom]
        : (defiInfo?.team1.filter((p) => !/^bot[1-9]$/i.test(p)) ?? []);
      const t2 = table.team2?.nom
        ? [table.team2.nom]
        : (defiInfo?.team2.filter((p) => !/^bot[1-9]$/i.test(p)) ?? []);
      if (t1.length + t2.length === 0) return null;
      return {
        team1: t1,
        team2: t2,
        onlyBots1: Boolean(defiInfo?.team1OnlyBots) && !table.team1?.nom,
        onlyBots2: Boolean(defiInfo?.team2OnlyBots) && !table.team2?.nom,
        photo1: table.team1?.photoUrl
          || (t1[0] ? `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(t1[0])}` : null),
        photo2: table.team2?.photoUrl
          || (t2[0] ? `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(t2[0])}` : null),
      };
    }
    if (!defiInfo) return null;
    const t1 = defiInfo.team1.filter((p) => !/^bot[1-9]$/i.test(p));
    const t2 = defiInfo.team2.filter((p) => !/^bot[1-9]$/i.test(p));
    const totalHumans = t1.length + t2.length;
    if (totalHumans === 0 || totalHumans > 4) return null;
    return {
      team1: t1,
      team2: t2,
      onlyBots1: defiInfo.team1OnlyBots,
      onlyBots2: defiInfo.team2OnlyBots,
      photo1: null as string | null,
      photo2: null as string | null,
    };
  })() : null;

  // Player names for libre tables
  const playerNames = (table.idserv === 'libre' && !isDraft) ? allPlayerNames : [];

  const canJoin = defiAccessible && (!isFull || isDiffereOrLive || isDefi || isDraft);

  // Vintage seat icon tooltip label
  const seatTooltipLabel = isDiffereOrLive
    ? (playerCount > 0 ? `${playerCount} joueur${playerCount > 1 ? 's' : ''} connecté${playerCount > 1 ? 's' : ''}` : 'Aucun joueur connecté')
    : `${Math.min(playerCount, maxPlayers)}/${maxPlayers} places occupées`;
  const cubeLabel = (() => {
    if (isDiffere) return 'Table différée — toujours accessible';
    const info = getTableStatusInfo(table);
    return info.label;
  })();

  return (
    <div
      className={`all-tables-row ${canJoin ? 'clickable' : 'full'} ${isDraft ? 'draft' : ''} ${isMyDraft ? 'my-draft' : ''} ${defiFiltered ? 'filtered' : ''} ${isLiveTournament ? 'tournament-glow' : ''} ${isExpectedForMe ? 'defi-awaits-you' : ''} ${(userIsConnected || isMyDraft) ? 'user-connected' : ''}`}
      onClick={canJoin ? onJoin : undefined}
    >
      {/* Status indicator: vintage or modern */}
      {vintageMode ? (
        <TableTooltip content={
          <div className="vintage-tooltip">
            <div className="vintage-tooltip-row">{seatTooltipLabel}</div>
            {!isDiffere && <div className="vintage-tooltip-row">{cubeLabel}</div>}
          </div>
        }>
          <span className="all-tables-vintage-indicators">
            <VintageSeatIcon nbj={maxPlayers} connectedCount={playerCount} isDiffereOrLive={isDiffereOrLive} />
            {isDiffere
              ? <span className="vintage-status-cube" style={{ visibility: 'hidden' }} />
              : <VintageStatusCube table={table} />
            }
          </span>
        </TableTooltip>
      ) : (
        <TableTooltip content={<span className="status-tooltip-text">{status.label}</span>}>
          <span
            className={`all-tables-status-dot ${status.pulse ? 'pulse' : ''}`}
            style={{ '--dot-color': status.color } as React.CSSProperties}
          />
        </TableTooltip>
      )}

      {/* FFT badge */}
      {!isDraft && table.idserv !== 'libre' && (
        <span className="all-tables-fft-badge" title="Table FFT">FFT</span>
      )}

      {/* Entry mode icons */}
      {(table.modeEntree === 2 || defiFiltered) && (
        <span className="all-tables-entry-icon" title={defiFiltered ? 'Défi privé — vous n\'en faites pas partie' : 'Entrée filtrée par le maître de table'}>🔍</span>
      )}
      {table.modeEntree === 3 && (
        <span className="all-tables-entry-icon" title="Table protégée par mot de passe">🔒</span>
      )}

      {/* Draft badge */}
      {isDraft && (
        <span className={`all-tables-draft-badge ${isMyDraft ? 'my-draft' : ''}`}>
          {isMyDraft ? 'Ma table' : 'Brouillon'}
        </span>
      )}

      {/* Sub-type icon for différé individuel (game mode) */}
      {table.idserv === 'diff_indiv' && getDiffereSubTypeIcon(displayName, 14) && (
        <span className="differe-subtype-icon" style={{ color: getTableTypeColor(table.idserv) }}>
          {getDiffereSubTypeIcon(displayName, 14)}
        </span>
      )}

      {/* Sub-type icon for différé équipe/triplette (team type) */}
      {table.idserv === 'diff_tq' && getDiffTqTypeIcon(table.nomtable, 14) && (
        <span className="differe-subtype-icon" style={{ color: getTableTypeColor(table.idserv) }}>
          {getDiffTqTypeIcon(table.nomtable, 14)}
        </span>
      )}

      {/* Table name — always visible, never shrinks below readable */}
      <span className="all-tables-row-name">{displayName}</span>
      {isCiaDefi && (
        <span
          className={`all-tables-cia-badge ${table.isBarrage || table.phaseEtat === 'barrage' ? 'barrage' : ''}`}
          title={table.phaseNom || 'Championnat Individuel Annuel'}
        >
          {getCiaContextLabel(table) || 'CIA'}
        </span>
      )}

      {/* Right-side info zone — shrinks/hides first on small screens */}
      <span className="all-tables-row-right">
        {/* Defi: équipes (noms) ou CIA/libre (joueurs 1v1) — pas de composition 8 joueurs en liste */}
        {defiAvatars && (
          <span className="all-tables-defi-avatars">
            {defiAvatars.team1.map((p, i) => (
              <span key={`t1-${i}`} className="all-tables-defi-player">
                <PlayerAvatar
                  pseudo={p}
                  size={18}
                  className="all-tables-defi-avatar"
                  {...(isTeamDefi || defiAvatars.photo1
                    ? { photoUrl: defiAvatars.photo1 || `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(p)}` }
                    : {})}
                />
                <span className="all-tables-defi-name">{decodeHtmlEntities(p)}</span>
              </span>
            ))}
            {defiAvatars.onlyBots1 && <span className="all-tables-defi-bot">🤖</span>}
            <span className="all-tables-defi-vs">⚔️</span>
            {defiAvatars.team2.map((p, i) => (
              <span key={`t2-${i}`} className="all-tables-defi-player">
                <PlayerAvatar
                  pseudo={p}
                  size={18}
                  className="all-tables-defi-avatar"
                  {...(isTeamDefi || defiAvatars.photo2
                    ? { photoUrl: defiAvatars.photo2 || `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(p)}` }
                    : {})}
                />
                <span className="all-tables-defi-name">{decodeHtmlEntities(p)}</span>
              </span>
            ))}
            {defiAvatars.onlyBots2 && <span className="all-tables-defi-bot">🤖</span>}
          </span>
        )}

        {/* Format badge (à Xj) */}
        {(isDiffereOrLive || isDefi) && (
          <span className="all-tables-format-badge">à {maxPlayers}j</span>
        )}

        {/* Secondary info (comm, score, etc.) — sans composition équipe */}
        {secondaryInfo && (
          <span className={`all-tables-row-info ${isLiveTournament ? 'tournament-info' : ''}`}>
            {tournamentHighlight ? (
              <>{tournamentHighlight.before}<strong className="tournament-minutes">{tournamentHighlight.minutes}</strong></>
            ) : secondaryInfo}
          </span>
        )}

        {/* Connected players: special display for live/différé */}
        {isDiffereOrLive ? (
          <ConnectedPlayersCell names={allPlayerNames} />
        ) : (
          /* Libre/defi: adaptive player list or count */
          playerNames.length > 0 ? (
            <AdaptivePlayerList names={playerNames} />
          ) : !isDefi ? (
            <TableTooltip content={<PlayerListTooltip names={allPlayerNames} />}>
              <span className="all-tables-row-players">{playerCount}/{maxPlayers}</span>
            </TableTooltip>
          ) : null
        )}
      </span>

      {/* Join action */}
      <span className={`all-tables-row-action ${isExpectedForMe ? 'defi-awaits-you-action' : ''}`}>
        {!defiAccessible ? '' : isExpectedForMe ? 'On vous attend ›' : isDraft ? 'Rejoindre ›' : isFull ? 'Complète' : 'Rejoindre ›'}
      </span>
    </div>
  );
}

/** Connected players on a card: avatars+names for ≤2, avatars-only with tooltip for 3+, capped at 19 + "…" */
function CardConnectedPlayers({ names }: { names: string[] }) {
  const [hoverVisible, setHoverVisible] = useState(false);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number; placement: 'above' | 'below' }>({ x: 0, y: 0, placement: 'above' });
  const listRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showTooltip = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      if (listRef.current) {
        const rect = listRef.current.getBoundingClientRect();
        const spaceAbove = rect.top;
        if (spaceAbove < 120) {
          setHoverPos({ x: rect.left, y: rect.bottom + 4, placement: 'below' });
        } else {
          setHoverPos({ x: rect.left, y: rect.top - 4, placement: 'above' });
        }
      }
      setHoverVisible(true);
    }, 300);
  }, []);

  const hideTooltip = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHoverVisible(false), 100);
  }, []);

  if (names.length <= 2) {
    return (
      <div className="table-card-connected-list">
        {names.map((name, idx) => (
          <span key={idx} className="player-tag">
            <PlayerAvatar pseudo={name} size={16} className="player-tag-avatar" />
            {decodeHtmlEntities(name)}
          </span>
        ))}
      </div>
    );
  }

  const maxShow = names.length > 19 ? 19 : names.length;
  const hasMore = names.length > 19;
  const transformStyle = hoverPos.placement === 'below'
    ? 'translate(-100%, 0)'
    : 'translate(-100%, -100%)';

  return (
    <>
      <div
        ref={listRef}
        className="table-card-connected-list"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {names.slice(0, maxShow).map((name, idx) => (
          <span key={idx} className="player-tag player-tag-compact" title={decodeHtmlEntities(name)}>
            <PlayerAvatar pseudo={name} size={16} className="player-tag-avatar" />
          </span>
        ))}
        {hasMore && (
          <span className="player-tag more">…</span>
        )}
      </div>
      {hoverVisible && (
        <div
          className="table-tooltip"
          style={{ left: hoverPos.x, top: hoverPos.y, transform: transformStyle }}
          onMouseEnter={() => clearTimeout(hoverTimeout.current)}
          onMouseLeave={hideTooltip}
        >
          <div className="table-tooltip-content">
            <PlayerListTooltip names={names} />
          </div>
        </div>
      )}
    </>
  );
}

// Composant pour afficher une table en carte
function RetroDiffereTableCard({ pseudo, onOpen }: { pseudo: string; onOpen: () => void }) {
  const { activePlayerCount, activePlayers, secondaryInfo } = useRetroDiffereTableInfo(pseudo, !!pseudo);

  return (
    <div className="table-card available retro-differe-table-card" onClick={onOpen}>
      <div className="table-card-header">
        <span className="table-card-icon" style={{ color: getTableTypeColor('diff_indiv') }}>
          <AttackIcon size={20} />
        </span>
        <span className="table-card-title">Rétro classique</span>
        <span className="table-format-badge">à 4j</span>
      </div>
      <div className="table-card-body">
        <div className="table-card-players">
          <span className="player-count">{activePlayerCount}</span>
          <span className="player-label">
            joueur{activePlayerCount > 1 ? 's' : ''} actif{activePlayerCount > 1 ? 's' : ''}
          </span>
        </div>
        {activePlayers.length > 0 && (
          <CardConnectedPlayers names={activePlayers} />
        )}
        {secondaryInfo && (
          <div className="table-card-comment">
            {secondaryInfo}
          </div>
        )}
      </div>
      <div className="table-card-footer">
        <button className="join-btn" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Jouer
        </button>
      </div>
    </div>
  );
}

function TableCard({ table, onJoin, onWatch, currentPseudo }: {
  table: GameTable;
  onJoin: () => void;
  onWatch?: () => void;
  currentPseudo: string;
}) {
  const { expectedDefiTableKeys } = useApp();
  const draftTable = table as GameTable & { _isDraft?: boolean; _draftId?: string };
  const isDraft = draftTable._isDraft === true;
  const neoMeta = table as GameTable & {
    neo?: boolean;
    rejoindreEnCours?: boolean;
    dealEnCours?: number;
  };
  const canWatchAsSpectator = !!(
    neoMeta.neo
    && neoMeta.rejoindreEnCours
    && (neoMeta.dealEnCours ?? 0) > 0
    && onWatch
  );

  const playerCount = table.joueurs?.length || 0;
  const maxPlayers = table.nbj || 4;
  
  const isDiffereOrLive = table.idserv === 'diff_indiv' || table.idserv === 'diff_tq' || table.idserv === 'live';
  const isLive = table.idserv === 'live';
  const isDefi = table.idserv === 'defis' && !isDraft;
  const isFull = !isDiffereOrLive && !isDefi && !isDraft && playerCount >= maxPlayers;
  const userIsConnected = isUserInTable(table, currentPseudo);
  const isMyDraft = isDraft && isUserInDraftTable(table, currentPseudo);
  const isExpectedForMe = isDefi && !userIsConnected
    && expectedDefiTableKeys.has(`${table.ip}:${table.port}`);
  
  const defiInfo = isDefi ? parseDefiInfo(table.nomtable, table.comm) : null;
  const defiKind = isDefi ? getTableDefiKind(table) : null;
  const isOpenDefi = defiKind === 'ouvert'
    || Boolean(defiInfo && defiInfo.type.toLowerCase().includes('ouvert'));
  const defiAccessible = canAccessDefiTable({
    isDefi,
    isDraft,
    isOpenDefi,
    defiInfo,
    defiKind,
    table,
    pseudo: currentPseudo,
    isExpectedForMe,
  });
  const canJoinDefi = !isDefi || defiAccessible;
  
  // Live deal progress
  const liveDealProgress = isLive ? parseDealProgress(table.comm) : null;
  
  // Tournament detection for live cards
  const commDecoded = table.comm ? decodeHtmlEntities(table.comm) : '';
  const isLiveTournament = isLive && /tournoi\s+.*dans\s+moins\s+de\s+\d+\s+minute/i.test(commDecoded);
  
  let title = '';
  if (isDiffereOrLive) {
    title = extractTableTitle(table.nomtable, table.idserv);
  } else if (table.idserv === 'libre') {
    const firstPlayer = table.joueurs?.[0];
    if (firstPlayer && Array.isArray(firstPlayer) && firstPlayer[1]) {
      title = `Table de ${firstPlayer[1]}`;
    } else if (firstPlayer && !Array.isArray(firstPlayer)) {
      title = `Table de ${firstPlayer.pseudo_display || decodePseudo(firstPlayer.pseudo || '')}`;
    } else {
      title = 'Table libre';
    }
  } else if (isDefi) {
    title = getTableDefiLabel(table);
  } else {
    title = table.nomtable ? decodeHtmlEntities(table.nomtable) : 'Table';
  }
  
  const allPlayerNames = getTablePlayerNames(table);
  const playerNames = table.joueurs?.map(j => {
    if (Array.isArray(j)) return j[1] || j[0];
    return j.pseudo_display || decodePseudo(j.pseudo || '');
  }).filter(Boolean) || [];
  
  // Comm text stripped of redundant connected count and deal info for live
  let cardComm = commDecoded ? stripConnectedCount(commDecoded) : '';
  if (isLive && liveDealProgress) {
    cardComm = cardComm.replace(/,?\s*donne\s*n?°?\s*\d+\s*\/\s*\d+/gi, '').replace(/,\s*$/, '').trim();
  }

  // Glow classes
  const glowClass = isLiveTournament
    ? 'tournament-glow-card'
    : isExpectedForMe
      ? 'defi-awaits-you-card'
      : (userIsConnected || isMyDraft)
        ? 'user-connected-card my-draft-card'
        : '';
  
  // Rendu spécial pour les tables brouillon
  if (isDraft) {
    return (
      <div className={`table-card draft-card available ${glowClass}`} onClick={onJoin}>
        <div className="table-card-header">
          <span className="table-card-icon">📝</span>
          <span className="table-card-title">
            {table.nomtable ? decodeHtmlEntities(table.nomtable) : 'Table en préparation'}
          </span>
          <span className={`draft-badge-card ${isMyDraft ? 'my-draft' : ''}`}>
            {isMyDraft ? 'Ma table' : 'Brouillon'}
          </span>
        </div>

        <div className="table-card-body">
          <div className="table-card-players">
            <span className="player-count">{playerCount}/{maxPlayers}</span>
            <span className="player-label">joueurs</span>
          </div>

          {table.comm && (
            <div className="table-card-comment">
              {decodeHtmlEntities(table.comm)}
            </div>
          )}

          {playerNames.length > 0 && (
            <div className="table-card-player-list">
              {playerNames.slice(0, 5).map((name, idx) => (
                <span key={idx} className="player-tag">{name}</span>
              ))}
            </div>
          )}
        </div>

        <div className="table-card-footer">
          <button className="join-btn draft-join-btn" onClick={(e) => { e.stopPropagation(); onJoin(); }}>
            Rejoindre la préparation
          </button>
        </div>
      </div>
    );
  }

  // Rendu spécial pour les défis
  if (isDefi && defiInfo) {
    const hasScore = defiInfo.score1 !== undefined && defiInfo.score2 !== undefined;
    const team1Winning = hasScore && (defiInfo.score1 || 0) > (defiInfo.score2 || 0);
    const team2Winning = hasScore && (defiInfo.score2 || 0) > (defiInfo.score1 || 0);
    const hasProgress = defiInfo.currentDeal !== undefined && defiInfo.totalDeals !== undefined;
    const progressPercent = hasProgress ? ((defiInfo.currentDeal || 0) / (defiInfo.totalDeals || 1)) * 100 : 0;
    const kind = getTableDefiKind(table);
    const isTeamDefi = isTeamDefiKind(kind, defiInfo);
    const isCiaDefi = kind === 'cia';
    const teamParsed = isTeamDefi ? parseTeamDefiComment(table.comm) : null;
    const ciaCtx = isCiaDefi ? getCiaContextLabel(table) : null;

    const side1Labels = isTeamDefi && table.team1?.nom
      ? [table.team1.nom]
      : defiInfo.team1;
    const side2Labels = isTeamDefi && table.team2?.nom
      ? [table.team2.nom]
      : defiInfo.team2;
    const photo1 = isTeamDefi
      ? (table.team1?.photoUrl || (side1Labels[0]
        ? `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(side1Labels[0])}`
        : null))
      : null;
    const photo2 = isTeamDefi
      ? (table.team2?.photoUrl || (side2Labels[0]
        ? `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(side2Labels[0])}`
        : null))
      : null;

    // Matchup : noms d'équipes OU joueurs (CIA / libre)
    const renderMatchupSide = (
      team: string[],
      onlyBots: boolean,
      teamPhotoUrl: string | null,
    ) => {
      if (onlyBots) return <span className="defi-robot" title="Équipe de robots">🤖</span>;
      if (team.length === 0) return <span className="defi-player empty">—</span>;
      return team.map((p, i) => (
        <div key={i} className={`defi-player-row ${isTeamDefi ? 'defi-team-name-row' : ''}`}>
          <PlayerAvatar
            pseudo={p}
            size={isTeamDefi ? 28 : 22}
            className="defi-player-avatar"
            {...(teamPhotoUrl || isTeamDefi
              ? { photoUrl: teamPhotoUrl || `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(p)}` }
              : {})}
          />
          <span className={`defi-player ${isTeamDefi ? 'defi-team-name' : ''}`}>
            {decodeHtmlEntities(p)}
          </span>
        </div>
      ));
    };

    // Composition équipe : avatars seuls (carte)
    const lineupAvatars = (() => {
      if (!isTeamDefi) return null;
      const fromComm = [
        ...(teamParsed?.lineup1 || []),
        ...(teamParsed?.lineup2 || []),
      ].filter((p) => !/^bot[1-9]$/i.test(p));
      const fromConnected = allPlayerNames.filter((p) => !/^bot[1-9]$/i.test(p));
      const names = fromComm.length > 0 ? fromComm : fromConnected;
      if (names.length === 0) return null;
      return names;
    })();

    const defiStatus = isTeamDefi
      ? (teamParsed?.status || '')
      : stripTrailingPseudoList(stripConnectedCount(commDecoded));

    return (
      <div
        className={`table-card defi ${canJoinDefi ? 'available' : 'full filtered'} ${glowClass}`}
        onClick={canJoinDefi ? onJoin : undefined}
      >
        <div className="table-card-header">
          <span
            className="table-card-icon"
            style={{ color: getDefiKindColor(kind) }}
          >
            {(kind === 'cia' || kind === 'championnat_equipe')
              ? <TrophyIcon size={20} />
              : getDefiIcon(defiInfo.type, 20)}
          </span>
          <span className="table-card-title">{title}</span>
          {isCiaDefi && (
            <span
              className={`table-defi-division-badge ${ciaCtx === 'Barrage' ? 'barrage' : ''}`}
              title={table.phaseNom || 'Championnat Individuel Annuel'}
            >
              {ciaCtx || 'CIA'}
            </span>
          )}
        </div>

        <div className="defi-teams">
          <div className={`defi-team ${team1Winning ? 'winning' : ''}`}>
            <div className="defi-team-players">
              {renderMatchupSide(
                side1Labels,
                Boolean(defiInfo.team1OnlyBots) && !table.team1?.nom,
                photo1,
              )}
            </div>
            {hasScore && (
              <div className="defi-team-score">{defiInfo.score1?.toLocaleString('fr-FR')}</div>
            )}
          </div>

          <div className="defi-vs">⚔️</div>

          <div className={`defi-team ${team2Winning ? 'winning' : ''}`}>
            <div className="defi-team-players">
              {renderMatchupSide(
                side2Labels,
                Boolean(defiInfo.team2OnlyBots) && !table.team2?.nom,
                photo2,
              )}
            </div>
            {hasScore && (
              <div className="defi-team-score">{defiInfo.score2?.toLocaleString('fr-FR')}</div>
            )}
          </div>
        </div>

        {lineupAvatars && (
          <div className="defi-lineup-avatars" title={`${lineupAvatars.length} joueurs`}>
            {lineupAvatars.map((name, idx) => (
              <PlayerAvatar
                key={`${name}-${idx}`}
                pseudo={name}
                size={18}
                className="defi-lineup-avatar"
              />
            ))}
          </div>
        )}

        {defiStatus && (
          <div className="table-card-comment defi-status-line">{defiStatus}</div>
        )}

        {hasProgress && (
          <div className="defi-progress">
            <div className="defi-progress-track">
              <div className="defi-progress-line" />
              <div className="defi-progress-line-fill" style={{ width: `${progressPercent}%` }} />
              {Array.from({ length: defiInfo.totalDeals || 0 }).map((_, i) => (
                <div key={i}
                  className={`defi-progress-dot ${i < (defiInfo.currentDeal || 0) ? 'completed' : ''} ${i === (defiInfo.currentDeal || 0) - 1 ? 'current' : ''}`}
                  style={{ left: `${((i + 0.5) / (defiInfo.totalDeals || 1)) * 100}%` }}
                />
              ))}
            </div>
            <span className="defi-progress-text">{defiInfo.currentDeal}/{defiInfo.totalDeals}</span>
          </div>
        )}

        <div className="table-card-footer">
          {canJoinDefi ? (
            <button className={`join-btn ${isExpectedForMe ? 'defi-awaits-you-btn' : ''}`} onClick={(e) => { e.stopPropagation(); onJoin(); }}>
              {isExpectedForMe ? 'On vous attend !' : 'Rejoindre'}
            </button>
          ) : (
            <span className="table-status-full" title="Défi privé — vous n'en faites pas partie">Privé</span>
          )}
        </div>
      </div>
    );
  }
  
  // Rendu standard pour les autres tables (live, différé, libre)
  const liveProgressPercent = liveDealProgress
    ? (liveDealProgress.current / liveDealProgress.total) * 100
    : 0;

  return (
    <div className={`table-card ${isFull ? 'full' : 'available'} ${glowClass}`} onClick={!isFull ? onJoin : undefined}>
      <div className="table-card-header">
        <span
          className="table-card-icon"
          style={{
            color: isDefi && defiKind
              ? getDefiKindColor(defiKind)
              : getTableTypeColor(table.idserv),
          }}
        >
          {table.idserv === 'diff_tq' && getDiffTqTypeIcon(table.nomtable, 20)
            ? getDiffTqTypeIcon(table.nomtable, 20)
            : table.idserv === 'diff_indiv' && getDiffereSubTypeIcon(title, 20)
              ? getDiffereSubTypeIcon(title, 20)
              : (defiKind === 'cia' || defiKind === 'championnat_equipe')
                ? <TrophyIcon size={20} />
                : getTableIcon(table.idserv, 20)}
        </span>
        <span className="table-card-title">{title}</span>
        {isDiffereOrLive && (
          <span className="table-format-badge">à {maxPlayers}j</span>
        )}
      </div>
      
      <div className="table-card-body">
        <div className="table-card-players">
          {isDiffereOrLive ? (
            <>
              <span className="player-count">{playerCount}</span>
              <span className="player-label">joueur{playerCount > 1 ? 's' : ''} connecté{playerCount > 1 ? 's' : ''}</span>
            </>
          ) : (
            <>
              <span className="player-count">{playerCount}/{maxPlayers}</span>
              <span className="player-label">joueurs</span>
            </>
          )}
        </div>

        {/* Connected players list for live/différé */}
        {isDiffereOrLive && allPlayerNames.length > 0 && (
          <CardConnectedPlayers names={allPlayerNames} />
        )}
        
        {cardComm && (
          <div className={`table-card-comment ${isLiveTournament ? 'tournament-comment' : ''}`}>
            {cardComm}
          </div>
        )}

        {/* Live deal progress bar */}
        {liveDealProgress && (
          <div className="defi-progress">
            <div className="defi-progress-track">
              <div className="defi-progress-line" />
              <div className="defi-progress-line-fill" style={{ width: `${liveProgressPercent}%` }} />
              {liveDealProgress.total <= 24 && Array.from({ length: liveDealProgress.total }).map((_, i) => (
                <div key={i}
                  className={`defi-progress-dot ${i < liveDealProgress.current ? 'completed' : ''} ${i === liveDealProgress.current - 1 ? 'current' : ''}`}
                  style={{ left: `${((i + 0.5) / liveDealProgress.total) * 100}%` }}
                />
              ))}
            </div>
            <span className="defi-progress-text">{liveDealProgress.current}/{liveDealProgress.total}</span>
          </div>
        )}
        
        {playerNames.length > 0 && !isDiffereOrLive && (
          <div className="table-card-player-list">
            {playerNames.slice(0, 5).map((name, idx) => (
              <span key={idx} className="player-tag">
                <PlayerAvatar pseudo={name} size={16} className="player-tag-avatar" />
                {name}
              </span>
            ))}
            {playerNames.length > 5 && (
              <span className="player-tag more">+{playerNames.length - 5}</span>
            )}
          </div>
        )}
      </div>
      
      <div className="table-card-footer">
        {isFull ? (
          <span className="table-status-full">Complète</span>
        ) : (
          <>
            {canWatchAsSpectator && (
              <button
                className="join-btn join-btn-secondary"
                onClick={(e) => { e.stopPropagation(); onWatch!(); }}
              >
                Regarder
              </button>
            )}
            <button className="join-btn" onClick={(e) => { e.stopPropagation(); onJoin(); }}>
              Rejoindre
            </button>
          </>
        )}
      </div>
    </div>
  );
}
